import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  createProject,
  addCharacter,
  continueProject,
  deleteCharacter,
  deleteProject as deleteProjectApi,
  deleteScenesFrom as deleteScenesFromApi,
  bindCharacterImage,
  generateSceneImage,
  listReferenceAssets,
  uploadReferenceAsset,
  getSceneImageUrl,
  getSceneImageVersionUrl,
  getProject,
  getGenerationTask,
  listActiveGenerationTasks,
  listProjects,
  listSceneImages,
  getReferenceAssetUrl,
  deleteSceneImage,
  setSceneImagePrimary,
  updateCharacter,
  updateProject,
  updateProjectAudioConfig,
  generateSceneAudio,
  getSceneAudio,
  getAuthConfig,
  getAuthMe,
  getAccountUsage,
  editScene,
  DEFAULT_GUEST_ACCOUNT,
  ApiError,
  isProjectNotFoundError,
  logout as logoutApi,
  type ClientAudioGeneration,
  type ClientAudioSegment,
  type ClientProject,
  type ClientProjectSummary,
  type ClientReferenceAsset,
  type ClientScene,
  type SceneImage,
  type SceneImageModel,
  type SceneImageSource,
  type SceneEditRequest,
  type AuthConfig,
  type AuthMe,
} from './api';
import { visibleSceneSummary } from './scene-summary';
import type { ImageGenerationErrorResponse } from '../shared/types';
import type { UsagePage } from '../shared/billing';
import { canUseProjectTransfer, type EffectiveFeatures } from '../shared/account';
import { toImageErrorPanel, toImageErrorResponse } from './image-errors';
import { IMAGE_MODEL_LITE, IMAGE_MODEL_OPTIONS, type ImageModelId } from '../shared/image-models';
import type { CharacterGender, ProjectInput } from '../shared/types';
import type { StoryLength } from '../shared/story-length';
import { DEFAULT_TEXT_MODEL, TEXT_MODEL_OPTIONS, type TextModelId } from '../shared/text-models';
import { clearReaderPosition, getContinuationStartIndex, readReaderPosition, resolveReaderStartIndex, saveReaderPosition } from './reader';
import { ProjectRequestGate, type ProjectMutationToken } from './project-selection';
import {
  dispatchProjectListRetry,
  reusableCachedProject,
  type ProjectListRetryIntent,
} from './project-list';
import {
  ProjectResponseCoordinator,
  applyScopedChildProjectResult,
  type ChildProjectScope,
} from './project-response-coordinator';
import { parseRoute, routePath, routeProjectId as projectIdFromRoute, type AppRoute } from './routes';
import { invalidateCachedImage, loadCachedImage } from './image-cache';
import { shouldDismissOverlay } from './ui-behavior';
import { applySceneImageMutation } from './image-history';
import { getAudioPositionKey, readSavedAudioPosition, saveAudioPosition } from './audio-player';
import { VoiceLibraryModal, VoicePreviewButton } from './VoiceLibraryModal';
import yourStoryLogo from './assets/your-story-logo.svg';
import { GEMINI_VOICES, getGeminiVoice, voiceGenderLabel, type GeminiVoiceId, type ProjectAudioConfig } from '../shared/voices';
import { removeCharacterVoice, setCharacterVoice } from './character-config';
import { AuthModal } from './AuthModal';
import { ProfileModal } from './ProfileModal';
import { setTurnstileSiteKey } from './turnstile';
import { OperationController } from './operation-controller';
import { BillingPage } from './BillingPage';
import { ProjectExportModal, ProjectImportModal } from './ProjectTransferModal';
import { pollGenerationTask, type ClientGenerationTask, type GenerationTaskPollerOptions } from './generation-task';

const GEMINI_VOICE_OPTIONS = GEMINI_VOICES.map((voice) => voice.id);
const CREATE_PROJECT_MUTATION_SCOPE = 'create-project';

type ModalErrorSink = (message: string) => void;
type ContinueHandler = (instruction: string | undefined, length: StoryLength, model: TextModelId, onError?: ModalErrorSink) => Promise<boolean>;
type DeleteHandler = (sceneIndex: number) => Promise<boolean>;
type RewriteHandler = (sceneIndex: number, request: SceneEditRequest, onError?: ModalErrorSink) => Promise<boolean>;
type ProjectNotFoundHandler = (scope: ChildProjectScope, message: string) => void;
type GenerateImageHandler = (scope: ChildProjectScope, sceneIndex: number, source: SceneImageSource, model: SceneImageModel, customPrompt?: string, referenceImageDataUrl?: string, referenceAssetIds?: string[], signal?: AbortSignal) => Promise<ImageGenerationErrorResponse | null | undefined>;
type ProjectEditorDraft = Pick<ClientProject, 'title' | 'summary'>;
type CharacterConfigDraft = ClientProject['characters'][number] & { voice: GeminiVoiceId };

const defaultForm = {
  protagonistName: '',
  personality: '谨慎, 好奇',
  hobbies: '旧书',
  occupation: '',
  genre: '都市悬疑',
  tone: '克制紧张',
  length: 'short' as const,
  idea: '',
};

function splitTags(value: string) {
  return value.split(/[，,、]/).map((item) => item.trim()).filter(Boolean);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric' }).format(new Date(value));
}

function StatusMessage({ message }: { message: string }) {
  return <p className="status-message" role="status">{message}</p>;
}

function ModalErrorNotice({ message, onRequireLogin }: { message: string; onRequireLogin?: () => void }) {
  const showLogin = Boolean(onRequireLogin && message.includes('余额不足'));
  return <div className="modal-error" role="alert"><span>{message}</span>{showLogin && <button className="modal-login-link" type="button" onClick={onRequireLogin}>登录</button>}</div>;
}

function ModalGenerationProgress({ busy, progress }: { busy: boolean; progress?: ClientGenerationTask | null }) {
  if (!busy) return null;
  const message = progress?.status === 'queued'
    ? '已加入生成队列'
    : progress
      ? `正在生成… ${progress.progress}%`
      : '正在准备生成…';
  return <div className="modal-generation-progress" role="status"><span className="sync-spinner" />{message}</div>;
}

function isAbortError(cause: unknown) {
  return cause instanceof DOMException && cause.name === 'AbortError';
}

export function visibleFeatures(account: AuthMe): EffectiveFeatures {
  if (account.authenticated) return account.features;
  return {
    ...account.features,
    image: { ...account.features.image, enabled: false, reason: '登录并充值后可用' },
    audio: { ...account.features.audio, enabled: false, reason: '登录并充值后可用' },
  };
}

type SceneAudioPanelError = {
  code?: string;
  title?: string;
  message: string;
  suggestion?: string;
  retryable?: boolean;
};

function toSceneAudioPanelError(cause: unknown, fallback: string): SceneAudioPanelError {
  if (cause instanceof ApiError) {
    const { payload } = cause;
    return {
      code: typeof payload.code === 'string' ? payload.code : undefined,
      title: typeof payload.error === 'string' ? payload.error : undefined,
      message: typeof payload.message === 'string' ? payload.message : cause.message,
      suggestion: typeof payload.suggestion === 'string' ? payload.suggestion : undefined,
      retryable: typeof payload.retryable === 'boolean' ? payload.retryable : undefined,
    };
  }
  return { message: cause instanceof Error ? cause.message : fallback };
}

function SceneAudioErrorNotice({ failure }: { failure: SceneAudioPanelError }) {
  return <div className="scene-audio-error" role="alert">
    {(failure.title || failure.code) && <div><strong>{failure.title || failure.code}</strong>{failure.title && failure.code && <> · <span>{failure.code}</span></>}</div>}
    <p>{failure.message}</p>
    {failure.suggestion && <small>建议：{failure.suggestion}</small>}
  </div>;
}

function SceneAudioPlayer({ projectId, sceneIndex, src, segmentCount, speakerNames }: { projectId: string; sceneIndex: number; src: string; segmentCount: number; speakerNames: string[] }) {
  const [speed, setSpeed] = useState(1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const positionKey = `${getAudioPositionKey(projectId, sceneIndex)}:combined`;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = speed;
    const saved = typeof window === 'undefined' ? 0 : readSavedAudioPosition(window.localStorage, positionKey);
    const restore = () => {
      if (saved > 0 && Number.isFinite(audio.duration) && saved < audio.duration) audio.currentTime = saved;
    };
    audio.addEventListener('loadedmetadata', restore);
    return () => audio.removeEventListener('loadedmetadata', restore);
  }, [positionKey, src, speed]);

  return <div className="scene-audio-player">
    <audio
      ref={audioRef}
      controls
      preload="metadata"
      src={src}
      onTimeUpdate={(event) => {
        if (typeof window !== 'undefined') saveAudioPosition(window.localStorage, positionKey, event.currentTarget.currentTime);
      }}
      onRateChange={(event) => setSpeed(event.currentTarget.playbackRate)}
    />
    <div className="scene-audio-player-meta">
      <span>整幕音频 · 已合并 {segmentCount} 个片段 · {speakerNames.join('、')}</span>
      <label>速度 <select value={speed} onChange={(event) => setSpeed(Number(event.target.value))} aria-label="播放速度"><option value="0.75">0.75×</option><option value="1">1×</option><option value="1.25">1.25×</option><option value="1.5">1.5×</option></select></label>
    </div>
  </div>;
}

export function SceneAudioPanel({ projectId, sceneIndex, requestScope, onProjectNotFound }: { projectId: string; sceneIndex: number; requestScope: ChildProjectScope; onProjectNotFound: ProjectNotFoundHandler }) {
  const [generation, setGeneration] = useState<ClientAudioGeneration | null>(null);
  const [segments, setSegments] = useState<ClientAudioSegment[]>([]);
  const [combinedUrl, setCombinedUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<SceneAudioPanelError | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const requestRef = useRef(0);
  const generationOperations = useRef(new OperationController());
  const generationAbort = useRef<AbortController | undefined>(undefined);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const controller = new AbortController();
    let timer: number | undefined;
    async function load() {
      try {
        const manifest = await getSceneAudio(projectId, sceneIndex, controller.signal);
        if (requestId !== requestRef.current) return;
        setGeneration(manifest.generation);
        setSegments(manifest.segments);
        setCombinedUrl(manifest.combinedUrl || '');
        setLoading(false);
        if (manifest.generation?.status === 'queued' || manifest.generation?.status === 'running') {
          timer = window.setTimeout(load, 1800);
        }
      } catch (cause) {
        if (requestId === requestRef.current && !(cause instanceof DOMException && cause.name === 'AbortError')) {
          if (isProjectNotFoundError(cause)) {
            onProjectNotFound(requestScope, '这部小说已经不存在，无法读取本幕配音。');
          } else {
            setError(toSceneAudioPanelError(cause, '无法读取本幕配音状态'));
          }
          setLoading(false);
        }
      }
    }
    void load();
    return () => {
      requestRef.current += 1;
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [projectId, refreshNonce, requestScope, sceneIndex]);

  useEffect(() => () => {
    generationOperations.current.invalidate();
    generationAbort.current?.abort();
    generationAbort.current = undefined;
  }, [projectId, requestScope, sceneIndex]);

  async function handleGenerate() {
    if (submitting || generationOperations.current.isBusy() || generation?.status === 'queued' || generation?.status === 'running') return;
    setSubmitting(true);
    setError(null);
    if (generation?.status === 'failed') setGeneration(null);
    generationAbort.current?.abort();
    const operation = generationOperations.current.beginBusy();
    const controller = new AbortController();
    generationAbort.current = controller;
    try {
      const next = await generateSceneAudio(projectId, sceneIndex, controller.signal);
      if (!generationOperations.current.isCurrent(operation)) return;
      setGeneration(next);
      setSegments([]);
      setCombinedUrl('');
      setRefreshNonce((nonce) => nonce + 1);
    } catch (cause) {
      if (!generationOperations.current.isCurrent(operation) || isAbortError(cause)) return;
      if (isProjectNotFoundError(cause)) {
        onProjectNotFound(requestScope, '这部小说已经不存在，无法生成本幕配音。');
      } else {
        setError(toSceneAudioPanelError(cause, '本幕配音生成失败'));
      }
    } finally {
      if (generationAbort.current === controller) generationAbort.current = undefined;
      const ownedBusy = generationOperations.current.finishBusy(operation);
      if (mounted.current && ownedBusy) setSubmitting(false);
    }
  }

  const failedError = generation?.error;
  const working = submitting || generation?.status === 'queued' || generation?.status === 'running';
  return <section className="scene-audio-panel" aria-label="本幕配音">
    <div className="scene-audio-heading"><div><span className="section-kicker">本幕配音</span><h2>听这一幕</h2></div><span className="scene-audio-status">{loading ? '读取中…' : generation?.status === 'succeeded' ? '已完成' : generation?.status === 'failed' ? '生成失败' : '尚未生成'}</span></div>
    <p className="scene-audio-description">使用目录中已保存的旁白和角色音色生成，完成后可连续播放；不会自动消耗额度。</p>
    {error && <SceneAudioErrorNotice failure={error} />}
    {failedError && <SceneAudioErrorNotice failure={failedError} />}
    {working && <div className="scene-audio-progress" role="status"><span className="sync-spinner" />正在生成第 {generation?.completedSegments ?? 0} / {generation?.segmentCount ?? '…'} 个音频片段…</div>}
    {generation?.status === 'succeeded' && combinedUrl && segments.length > 0 && <SceneAudioPlayer projectId={projectId} sceneIndex={sceneIndex} src={combinedUrl} segmentCount={segments.length} speakerNames={[...new Set(segments.flatMap((segment) => segment.speakerNames))]} />}
    <button className="scene-audio-generate" type="button" disabled={working || loading} onClick={() => void handleGenerate()}>{working ? '正在生成…' : error?.retryable || failedError?.retryable ? '重试生成本幕配音' : generation ? '重新生成本幕配音' : '生成本幕配音'}</button>
  </section>;
}

function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [locked]);
}

function useEscapeToClose(onClose: () => void, locked: boolean) {
  useEffect(() => {
    if (locked) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [locked, onClose]);
}

export function StoryLengthSelector({ value, availableLengths, onChange, name = 'story-length' }: { value: StoryLength; availableLengths: StoryLength[]; onChange: (length: StoryLength) => void; name?: string }) {
  const options: Array<{ value: StoryLength; label: string; detail: string }> = [
    { value: 'short', label: '短篇', detail: '3—5 幕' },
    { value: 'medium', label: '中篇', detail: '2—3 幕' },
    { value: 'long', label: '长篇', detail: '1—2 幕' },
  ];
  return <select
    aria-label="篇幅"
    className="story-length-selector"
    name={name}
    onChange={(event) => {
      const nextLength = event.target.value as StoryLength;
      if (availableLengths.includes(nextLength)) onChange(nextLength);
    }}
    value={value}
  >{options.map((option) => {
    const allowed = availableLengths.includes(option.value);
    const optionLabel = `${option.label}（${option.detail}${allowed ? '' : '，会员可用'}）`;
    return <option disabled={!allowed} key={option.value} value={option.value}>
      {optionLabel}
    </option>;
  })}</select>;
}

export function TextModelSelector({ value, onChange, name = 'text-model' }: { value: TextModelId; onChange: (model: TextModelId) => void; name?: string }) {
  return <select
    aria-label="模型"
    className="story-length-selector text-model-selector"
    name={name}
    onChange={(event) => onChange(event.target.value as TextModelId)}
    value={value}
  >{TEXT_MODEL_OPTIONS.map((option) => <option key={option.id} value={option.id}>
    {option.label}（{option.description}）
  </option>)}</select>;
}

type StoryFormState = Omit<typeof defaultForm, 'length'> & { length: StoryLength };

export function StoryForm({ onCreate, onRequireLogin, guestTrialAvailable = false, requiresLogin = false, requiresBalance = false, storyLengths }: { onCreate: (input: ProjectInput, model: TextModelId) => Promise<void>; onRequireLogin?: () => void; guestTrialAvailable?: boolean; requiresLogin?: boolean; requiresBalance?: boolean; storyLengths: StoryLength[] }) {
  const [form, setForm] = useState<StoryFormState>(defaultForm);
  const [model, setModel] = useState<TextModelId>(DEFAULT_TEXT_MODEL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [balanceError, setBalanceError] = useState(false);

  const update = (key: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setBalanceError(false);
    setBusy(true);
    try {
      const input: ProjectInput = {
        protagonistName: form.protagonistName,
        personality: splitTags(form.personality),
        hobbies: splitTags(form.hobbies),
        occupation: form.occupation,
        genre: form.genre,
        tone: form.tone,
        length: form.length,
        idea: form.idea,
      };
      await onCreate(input, model);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'GUEST_TRIAL_USED') {
        onRequireLogin?.();
        setError('游客免费试用已使用，请先登录后继续生成。');
      } else if (cause instanceof ApiError && cause.code === 'INSUFFICIENT_BALANCE') {
        setBalanceError(true);
        setError('余额不足，请联系管理员获取内测额度。');
      } else {
        setError(cause instanceof Error ? cause.message : '故事生成失败');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="story-form" onSubmit={submit}>
      <div className="form-intro">
        <span className="section-kicker">01 / 故事种子</span>
        <h2>先从一个<br /><em>你自己</em>开始。</h2>
        <p>你的名字和选择，会成为故事里最先被记住的细节。</p>
      </div>

      <label>
        主角名字
        <input required maxLength={30} value={form.protagonistName} onChange={(event) => update('protagonistName', event.target.value)} placeholder="比如：林舟" />
      </label>
      <div className="form-row">
        <label>
          性格标签
          <input value={form.personality} onChange={(event) => update('personality', event.target.value)} placeholder="谨慎，好奇" />
        </label>
        <label>
          兴趣爱好
          <input value={form.hobbies} onChange={(event) => update('hobbies', event.target.value)} placeholder="旧书，摄影" />
        </label>
      </div>
      <div className="form-row">
        <label>
          现实中的职业
          <input value={form.occupation} onChange={(event) => update('occupation', event.target.value)} placeholder="可以留空" />
        </label>
        <label>
          故事类型
          <select value={form.genre} onChange={(event) => update('genre', event.target.value)}>
            <option>都市悬疑</option>
            <option>奇幻冒险</option>
            <option>温暖治愈</option>
            <option>科幻探索</option>
            <option>爱情成长</option>
          </select>
        </label>
      </div>
      <div className="story-length-field">
        篇幅
        <StoryLengthSelector value={form.length} availableLengths={storyLengths} onChange={(length) => setForm((current) => ({ ...current, length }))} name="new-story-length" />
      </div>
      <div className="story-length-field">
        模型
        <TextModelSelector value={model} onChange={setModel} name="new-story-model" />
      </div>
      <label>
        故事种子
        <textarea maxLength={1000} value={form.idea} onChange={(event) => update('idea', event.target.value)} placeholder="可以留空，让模型自由发挥……" />
      </label>
      <div className="form-footer">
        <span>{requiresLogin ? '游客试用已用完，请登录后继续生成。' : guestTrialAvailable ? '游客可免费生成 1 次文字小说，配图和配音需充值。' : requiresBalance ? <>当前余额不足，请联系管理员获取内测额度。{onRequireLogin && <button className="status-message-action" type="button" onClick={onRequireLogin}>登录</button>}</> : '篇幅决定本次生成的场景数量和展开程度，剧情完整优先，不会为了凑字数重复。'}</span>
        <button disabled={busy} type="submit">{busy ? '正在织入…' : '生成我的故事 →'}</button>
      </div>
      {error && (balanceError ? <div className="status-message" role="status"><span>{error}</span>{onRequireLogin && <button className="status-message-action" type="button" onClick={onRequireLogin}>登录</button>}</div> : <StatusMessage message={error} />)}
    </form>
  );
}

function ProjectRail({ projects, currentId, account, phoneLogin, canTransfer, loading, error, nextCursor, loadingMore, loadMoreBlocked, onSelect, onRetry, onLoadMore, onNew, onHome, onAccountAction, onImport }: {
  projects: ClientProjectSummary[];
  currentId?: string;
  account: AuthMe;
  phoneLogin: boolean;
  canTransfer: boolean;
  loading: boolean;
  error: string;
  nextCursor?: string;
  loadingMore: boolean;
  loadMoreBlocked: boolean;
  onSelect: (projectId: string) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onNew: () => void;
  onHome: () => void;
  onAccountAction: () => void;
  onImport: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  useBodyScrollLock(menuOpen);

  function selectProject(projectId: string) {
    setMenuOpen(false);
    onSelect(projectId);
  }

  function accountAction() {
    setMenuOpen(false);
    onAccountAction();
  }

  return (
    <aside className="project-rail">
      <div className="rail-header"><button className="brand-mark" type="button" onClick={onHome} aria-label="返回你的故事主页"><img aria-hidden="true" src={yourStoryLogo} /><strong>你的故事</strong></button><button aria-controls="storyweave-project-drawer" aria-expanded={menuOpen} className="mobile-stories-toggle" type="button" aria-label={menuOpen ? '关闭我的故事菜单' : '打开我的故事菜单'} onClick={() => setMenuOpen((open) => !open)}><span aria-hidden="true">☰</span><small>{projects.length}</small></button></div>
      {menuOpen && <div aria-hidden="true" className="mobile-rail-scrim" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, false)) setMenuOpen(false); }} />}
      <div aria-label="我的故事" className={menuOpen ? 'rail-drawer is-open' : 'rail-drawer'} id="storyweave-project-drawer" role="navigation">
        <div className="rail-create-actions"><button className="new-story" type="button" onClick={() => { setMenuOpen(false); onNew(); }}>＋ 新故事</button>{canTransfer && <button className="import-story" type="button" onClick={() => { setMenuOpen(false); onImport(); }}>导入小说</button>}</div>
        <div className="rail-label">回忆</div>
        <nav>
          {loading && projects.length === 0 && <div className="rail-skeletons" role="status" aria-label="正在读取故事列表">{[0, 1, 2].map((index) => <span className="rail-project-skeleton" key={index} />)}</div>}
          {!loading && projects.length === 0 && !error && <p className="empty-rail">还没有故事。<br />第一个从这里开始。</p>}
          {projects.map((project) => (
            <button className={`project-link ${project.id === currentId ? 'active' : ''}`} key={project.id} type="button" onClick={() => selectProject(project.id)}>
              <span>{project.title}</span>
              <small>{formatDate(project.updatedAt)}</small>
            </button>
          ))}
          {error && <div className="rail-list-error" role="status"><span>{error}</span><button type="button" onClick={onRetry}>重试</button></div>}
          {nextCursor && <button className="rail-load-more" disabled={loading || loadingMore || loadMoreBlocked} type="button" onClick={onLoadMore}>{loadingMore ? '正在读取…' : '加载更多'}</button>}
        </nav>
        <div className="rail-account">
          <button className="rail-account-button" disabled={!phoneLogin && !account.authenticated} type="button" onClick={accountAction}>
            <strong>{account.authenticated ? (account.nickname || account.maskedPhone) : phoneLogin ? '手机号登录' : '登录暂未配置'}</strong>
            <small>{account.authenticated ? `${account.nickname ? account.maskedPhone + ' · ' : ''}个人信息` : phoneLogin ? '登录后同步故事与进度' : '请稍后再试'}</small>
          </button>
        </div>
      </div>
    </aside>
  );
}

function ReferenceAssetGrid({ assets, selectedIds, onToggle }: { assets: ClientReferenceAsset[]; selectedIds: string[]; onToggle: (assetId: string) => void }) {
  if (assets.length === 0) return <p className="reference-assets-empty">还没有项目参考图。</p>;
  return (
    <div className="reference-asset-grid">
      {assets.map((asset) => {
        const selected = selectedIds.includes(asset.id);
        return <button className={`reference-asset-card ${selected ? 'selected' : ''}`} key={asset.id} type="button" aria-pressed={selected} onClick={() => onToggle(asset.id)}><img alt={asset.label} src={getReferenceAssetUrl(asset.projectId, asset.id)} /><span>{asset.label}</span>{selected && <i aria-hidden="true">✓</i>}</button>;
      })}
    </div>
  );
}

function readReferenceFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('参考图读取失败，请重试。'));
    reader.onerror = () => reject(new Error('参考图读取失败，请重试。'));
    reader.readAsDataURL(file);
  });
}

function CharacterReferenceModal({ character, assets, busy, onClose, onUpload, onConfirm }: { character: ClientProject['characters'][number]; assets: ClientReferenceAsset[]; busy: boolean; onClose: () => void; onUpload: (dataUrl: string, label: string) => Promise<ClientReferenceAsset>; onConfirm: (assetId: string | null) => Promise<void> }) {
  const [selectedId, setSelectedId] = useState(character.imageAssetId || '');
  const [pendingFile, setPendingFile] = useState<{ name: string; dataUrl: string }>();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const locked = busy || submitting;

  useBodyScrollLock(true);
  useEscapeToClose(onClose, locked);

  async function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    setError('');
    if (!file) return;
    if (!REFERENCE_IMAGE_TYPES.includes(file.type)) return setError('请上传 PNG、JPEG 或 WebP 图片。');
    if (file.size > MAX_REFERENCE_IMAGE_BYTES) return setError('参考图不能超过 8MB。');
    try {
      setPendingFile({ name: file.name, dataUrl: await readReferenceFile(file) });
      setSelectedId('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '参考图读取失败，请重试。');
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const asset = pendingFile ? await onUpload(pendingFile.dataUrl, pendingFile.name) : undefined;
      await onConfirm(asset?.id || selectedId || null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '角色图片保存失败');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="modal-backdrop reference-picker-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, locked)) onClose(); }}>
      <section className="continuation-modal reference-picker-modal" role="dialog" aria-modal="true" aria-labelledby="character-reference-title">
        <div className="modal-heading"><div><span className="section-kicker">角色参考图</span><h2 id="character-reference-title">为{character.name}<br /><em>绑定一张默认图。</em></h2></div><button className="modal-close" disabled={locked} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">生成包含该角色的场景时，系统会自动把这张图作为人物参考。</p>
        {pendingFile ? <div className="reference-image-preview"><img alt="待上传的角色参考图" src={pendingFile.dataUrl} /><div><strong>{pendingFile.name}</strong><button disabled={locked} type="button" onClick={() => setPendingFile(undefined)}>改为选择已有图片</button></div></div> : <ReferenceAssetGrid assets={assets} selectedIds={selectedId ? [selectedId] : []} onToggle={(assetId) => setSelectedId((current) => current === assetId ? '' : assetId)} />}
        <label className="reference-image-upload compact-upload"><input accept="image/png,image/jpeg,image/webp" disabled={locked} onChange={chooseFile} type="file" /><span>＋</span><strong>上传新的角色参考图</strong><small>上传后会保存到当前项目资产库</small></label>
        {selectedId && <button className="clear-reference-button" disabled={locked} type="button" onClick={() => setSelectedId('')}>移除当前绑定</button>}
        {error && <p className="reference-image-error" role="alert">{error}</p>}
        <div className="modal-actions"><button className="modal-cancel" disabled={locked} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={locked} type="submit" form="character-reference-form">{locked ? '正在保存…' : '保存角色图片'}</button></div>
        <form id="character-reference-form" onSubmit={submit} />
      </section>
    </div>,
    document.body,
  );
}

function ProjectDetailsModal({ project, onClose, onSave, busy }: { project: ClientProject; onClose: () => void; onSave: (draft: ProjectEditorDraft) => Promise<void>; busy: boolean }) {
  const [title, setTitle] = useState(project.title);
  const [summary, setSummary] = useState(project.summary);
  const [formError, setFormError] = useState('');

  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !summary.trim()) {
      setFormError('标题和概览不能为空。');
      return;
    }
    setFormError('');
    await onSave({ title: title.trim(), summary: summary.trim() });
  }

  return createPortal(
    <div className="modal-backdrop project-editor-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy)) onClose(); }}>
      <section className="continuation-modal project-details-modal" role="dialog" aria-modal="true" aria-labelledby="project-details-title">
        <div className="modal-heading"><div><span className="section-kicker">编辑故事</span><h2 id="project-details-title">编辑故事<br /><em>标题与概览。</em></h2></div><button aria-label="关闭故事编辑" className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">这里只修改故事的标题和总体概览。角色资料、参考图和音色请从角色配置进入。</p>
        <form onSubmit={submit}>
          <label className="modal-input-label">故事标题<input maxLength={100} name="title" required value={title} onChange={(event) => setTitle(event.target.value)} /></label>
          <label className="modal-input-label">故事概览<textarea maxLength={1000} name="summary" required value={summary} onChange={(event) => setSummary(event.target.value)} /></label>
          {formError && <p className="project-editor-error" role="alert">{formError}</p>}
          <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={busy} type="submit">{busy ? '正在保存…' : '保存资料 →'}</button></div>
        </form>
      </section>
    </div>,
    document.body,
  );
}

function NarratorConfigModal({ voice: initialVoice, onClose, onSave, busy }: { voice: GeminiVoiceId; onClose: () => void; onSave: (voice: GeminiVoiceId) => Promise<void>; busy: boolean }) {
  const [voice, setVoice] = useState<GeminiVoiceId>(initialVoice);
  const [voiceLibraryOpen, setVoiceLibraryOpen] = useState(false);
  const voiceInfo = getGeminiVoice(voice);

  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy || voiceLibraryOpen);

  return createPortal(
    <div className="modal-backdrop project-editor-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy || voiceLibraryOpen)) onClose(); }}>
      <section className="continuation-modal narrator-config-modal" role="dialog" aria-modal="true" aria-labelledby="narrator-config-title">
        <div className="modal-heading"><div><span className="section-kicker">旁白配置</span><h2 id="narrator-config-title">配置旁白<br /><em>故事必须有讲述者。</em></h2></div><button aria-label="关闭旁白配置" className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">旁白默认存在，负责场景描述和非对话内容；这里只调整它的固定音色。</p>
        <div className="configured-voice-row"><div><span>当前音色</span><strong>{voice}</strong><small>{voiceGenderLabel(voice)} · {voiceInfo?.style} · {voiceInfo?.description}</small></div><div><VoicePreviewButton voice={voice} /><button className="voice-select-open" type="button" onClick={() => setVoiceLibraryOpen(true)}>选择音色</button></div></div>
        <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={busy} type="button" onClick={() => void onSave(voice)}>{busy ? '正在保存…' : '保存旁白 →'}</button></div>
      </section>
      {voiceLibraryOpen && <VoiceLibraryModal speakerName="旁白" selectedVoice={voice} onClose={() => setVoiceLibraryOpen(false)} onSelect={setVoice} />}
    </div>,
    document.body,
  );
}

function CharacterConfigModal({ project, character, characterIndex, assets, defaultVoice, imageEnabled, audioEnabled, busy, onClose, onUploadReference, onSave, onDelete }: { project: ClientProject; character?: ClientProject['characters'][number]; characterIndex: number | null; assets: ClientReferenceAsset[]; defaultVoice: GeminiVoiceId; imageEnabled: boolean; audioEnabled: boolean; busy: boolean; onClose: () => void; onUploadReference: (dataUrl: string, label: string) => Promise<ClientReferenceAsset>; onSave: (draft: CharacterConfigDraft) => Promise<void>; onDelete?: () => Promise<void> }) {
  const [name, setName] = useState(character?.name || '');
  const [role, setRole] = useState(character?.role || '配角');
  const [description, setDescription] = useState(character?.description || '');
  const [gender, setGender] = useState<CharacterGender>(character?.gender ?? 'unknown');
  const [imageAssetId, setImageAssetId] = useState(character?.imageAssetId);
  const [voice, setVoice] = useState<GeminiVoiceId>(defaultVoice);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [voiceLibraryOpen, setVoiceLibraryOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [formError, setFormError] = useState('');
  const voiceInfo = getGeminiVoice(voice);
  const locked = busy || (imageEnabled && referenceOpen) || (audioEnabled && voiceLibraryOpen);

  useBodyScrollLock(true);
  useEscapeToClose(onClose, locked);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || !role.trim() || !description.trim()) {
      setFormError('请补全角色姓名、身份和人物介绍。');
      return;
    }
    if (project.characters.some((item, index) => item.name === name.trim() && index !== characterIndex)) {
      setFormError(`角色“${name.trim()}”已经存在，请换一个名字。`);
      return;
    }
    setFormError('');
    await onSave({
      name: name.trim(),
      role: role.trim(),
      description: description.trim(),
      gender,
      voiceTraits: character?.voiceTraits ?? [],
      imageAssetId,
      voice,
    });
  }

  const draftCharacter = {
    name: name.trim() || '新角色',
    role: role.trim() || '配角',
    description: description.trim() || '待补充',
    gender,
    voiceTraits: character?.voiceTraits ?? [],
    imageAssetId,
  };
  return createPortal(
    <div className="modal-backdrop project-editor-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, locked)) onClose(); }}>
      <section className="continuation-modal character-config-modal" role="dialog" aria-modal="true" aria-labelledby="character-config-title">
        <div className="modal-heading"><div><span className="section-kicker">角色配置</span><h2 id="character-config-title">{character ? `配置${character.name}` : '添加角色'}<br /><em>人物设定统一在这里。</em></h2></div><button aria-label="关闭角色配置" className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">人物资料用于后续续写{imageEnabled ? '，参考图用于生成插图' : ''}{audioEnabled ? '，固定音色用于区分旁白与角色对白' : ''}。</p>
        <form onSubmit={submit}>
          <div className={`character-config-grid ${imageEnabled ? '' : 'character-config-grid--details-only'}`}>
            {imageEnabled && <div className="character-config-media">
              {imageAssetId ? <img alt={`${name || '角色'}参考图`} src={getReferenceAssetUrl(project.id, imageAssetId)} /> : <span>暂无参考图</span>}
              <button disabled={busy} type="button" onClick={() => setReferenceOpen(true)}>{imageAssetId ? '更换参考图' : '选择参考图'}</button>
            </div>}
            <div className="character-config-fields">
              <label className="modal-input-label">角色姓名<input maxLength={30} name="characterName" value={name} onChange={(event) => setName(event.target.value)} /></label>
              <label className="modal-input-label">人物身份<input maxLength={40} name="characterRole" value={role} onChange={(event) => setRole(event.target.value)} /></label>
              <label className="modal-input-label">性别<select className="story-length-selector" name="characterGender" value={gender} onChange={(event) => setGender(event.target.value as CharacterGender)}><option value="unknown">未说明</option><option value="female">女</option><option value="male">男</option></select></label>
              <label className="modal-input-label character-config-description">人物介绍<textarea maxLength={300} name="characterDescription" value={description} onChange={(event) => setDescription(event.target.value)} /></label>
              {audioEnabled && <div className="configured-voice-row character-config-voice"><div><span>角色音色</span><strong>{voice}</strong><small>{voiceGenderLabel(voice)} · {voiceInfo?.style} · {voiceInfo?.description}</small></div><div><VoicePreviewButton voice={voice} /><button className="voice-select-open" type="button" onClick={() => setVoiceLibraryOpen(true)}>选择音色</button></div></div>}
            </div>
          </div>
          {formError && <p className="project-editor-error" role="alert">{formError}</p>}
          {confirmDelete && <div className="character-delete-confirm" role="alert"><span>删除角色后，后续配音将不再保留该角色音色。</span><button type="button" onClick={() => setConfirmDelete(false)}>取消</button><button type="button" onClick={() => void onDelete?.()}>确认删除</button></div>}
          <div className="modal-actions character-config-actions"><div>{characterIndex !== null && onDelete && <button className="character-config-delete" disabled={busy} type="button" onClick={() => setConfirmDelete(true)}>删除角色</button>}</div><div><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={busy} type="submit">{busy ? '正在保存…' : character ? '保存角色 →' : '添加角色 →'}</button></div></div>
        </form>
      </section>
      {imageEnabled && referenceOpen && <CharacterReferenceModal character={draftCharacter} assets={assets} busy={busy} onClose={() => setReferenceOpen(false)} onUpload={onUploadReference} onConfirm={async (assetId) => { setImageAssetId(assetId || undefined); setReferenceOpen(false); }} />}
      {audioEnabled && voiceLibraryOpen && <VoiceLibraryModal speakerName={name.trim() || '新角色'} selectedVoice={voice} onClose={() => setVoiceLibraryOpen(false)} onSelect={setVoice} />}
    </div>,
    document.body,
  );
}

type OutlineViewProps = {
  project: ClientProject;
  features: EffectiveFeatures;
  storyLengths: StoryLength[];
  assets: ClientReferenceAsset[];
  onRead: (sceneIndex?: number) => void;
  onDeleteFrom: DeleteHandler;
  onDeleteProject: () => Promise<boolean>;
  onUploadReference: (dataUrl: string, label: string) => Promise<ClientReferenceAsset>;
  onRewrite: RewriteHandler;
  onSaveProject: (draft: ProjectEditorDraft) => Promise<boolean>;
  onSaveCharacter: (characterIndex: number | null, draft: CharacterConfigDraft) => Promise<boolean>;
  onDeleteCharacter: (characterIndex: number) => Promise<boolean>;
  onSaveNarrator: (voice: GeminiVoiceId) => Promise<boolean>;
  continuing: boolean;
  deleting: boolean;
  deletingProject: boolean;
  projectEditBusy: boolean;
  rewriteBusySceneIndex: number | null;
  generationProgress: ClientGenerationTask | null;
  onExport: () => void;
  onImport: () => void;
  canTransfer: boolean;
};

function OutlineView({ project, features, storyLengths, assets, onRead, onDeleteFrom, onDeleteProject, onUploadReference, onRewrite, onSaveProject, onSaveCharacter, onDeleteCharacter, onSaveNarrator, continuing, deleting, deletingProject, projectEditBusy, rewriteBusySceneIndex, generationProgress, onExport, onImport, canTransfer }: OutlineViewProps) {
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [projectDeleteOpen, setProjectDeleteOpen] = useState(false);
  const [characterConfigIndex, setCharacterConfigIndex] = useState<number | 'new' | null>(null);
  const [narratorConfigOpen, setNarratorConfigOpen] = useState(false);
  const [rewriteSceneIndex, setRewriteSceneIndex] = useState<number | null>(null);
  const [projectEditorOpen, setProjectEditorOpen] = useState(false);
  const imageEnabled = features.image.enabled;
  const audioEnabled = features.audio.enabled;
  async function confirmDelete() {
    if (pendingDelete === null) return;
    if (await onDeleteFrom(pendingDelete)) setPendingDelete(null);
  }
  return (
    <section className="workspace outline-view">
      <header className="workspace-header">
        <div className="outline-meta"><span className="section-kicker">02 / 故事目录</span><div className="outline-status"><p className="muted">故事已生成 · {project.scenes.length} 个场景</p><div className="outline-meta-actions"><button className="story-edit-link" disabled={continuing || deletingProject || projectEditBusy} type="button" onClick={() => setProjectEditorOpen(true)}>编辑</button>{canTransfer && <><button className="story-edit-link" disabled={continuing || deletingProject || projectEditBusy} type="button" onClick={onExport}>导出</button><button className="story-edit-link" disabled={continuing || deletingProject || projectEditBusy} type="button" onClick={onImport}>导入</button></>}<button className="story-delete-link" disabled={continuing || deletingProject || projectEditBusy} type="button" onClick={() => setProjectDeleteOpen(true)}>删除小说</button></div></div></div>
        <div className="header-actions"><button disabled={deletingProject} type="button" onClick={() => onRead()}>进入阅读 →</button></div>
      </header>
      <div className="outline-heading"><h1>{project.title}</h1><p>{project.summary}</p></div>
      <div className="outline-grid">
        <div className="character-column">
          <div className="character-column-heading"><span className="section-kicker">人物</span><button disabled={projectEditBusy || project.characters.length >= 8} type="button" onClick={() => setCharacterConfigIndex('new')}>＋ 添加角色</button></div>
          {audioEnabled && <div className="character-line narrator-line"><div className="character-line-heading"><div><span className="narrator-avatar" aria-hidden="true">旁</span><strong>旁白</strong></div><button className="role-config-button" type="button" onClick={() => setNarratorConfigOpen(true)}>旁白配置</button></div><span>默认讲述者 · {project.audioConfig.narrator} · {voiceGenderLabel(project.audioConfig.narrator)}</span><p>负责场景描述和非对话内容，每个故事默认保留一名旁白。</p></div>}
          {project.characters.map((character, index) => <div className="character-line" key={`${character.name}-${index}`}><div className="character-line-heading"><div>{imageEnabled && character.imageAssetId ? <img className="character-reference-thumb" alt={`${character.name}参考图`} src={getReferenceAssetUrl(project.id, character.imageAssetId)} /> : <span className="character-reference-thumb character-reference-empty" aria-hidden="true">{character.name.slice(0, 1)}</span>}<strong>{character.name}</strong></div><button className="role-config-button" type="button" onClick={() => setCharacterConfigIndex(index)}>角色配置</button></div><span>{character.role}</span><p>{character.description}</p></div>)}
        </div>
        <div className="scene-column">
          <span className="section-kicker">章节 / 场景</span>
          <div className="scene-list">
            {project.scenes.map((scene) => <article className="scene-row" key={scene.index}><button aria-label={`阅读第 ${scene.index} 幕：${scene.title}`} className="scene-open" type="button" onClick={() => onRead(scene.index)} /><span className="scene-number">0{scene.index}</span><div><h3>{scene.title}</h3><p>{visibleSceneSummary(scene.summary)}</p></div><div className="scene-row-actions"><button className="scene-rewrite" disabled={deleting || rewriteBusySceneIndex !== null} type="button" onClick={() => setRewriteSceneIndex(scene.index)}>{rewriteBusySceneIndex === scene.index ? '重置中…' : '重置本幕'}</button><button className="scene-delete" disabled={deleting} type="button" onClick={() => setPendingDelete(scene.index)}>删至此</button></div><span aria-hidden="true" className="scene-arrow">↗</span></article>)}
          </div>
        </div>
      </div>
      {pendingDelete !== null && <DeleteScenesModal fromScene={pendingDelete} totalScenes={project.scenes.length} busy={deleting} onClose={() => setPendingDelete(null)} onConfirm={confirmDelete} />}
      {projectDeleteOpen && <DeleteProjectModal title={project.title} busy={deletingProject} onClose={() => setProjectDeleteOpen(false)} onConfirm={async () => { if (await onDeleteProject()) setProjectDeleteOpen(false); }} />}
      {audioEnabled && narratorConfigOpen && <NarratorConfigModal voice={project.audioConfig.narrator} busy={projectEditBusy} onClose={() => setNarratorConfigOpen(false)} onSave={async (voice) => { if (await onSaveNarrator(voice)) setNarratorConfigOpen(false); }} />}
      {characterConfigIndex !== null && <CharacterConfigModal key={characterConfigIndex} project={project} character={characterConfigIndex === 'new' ? undefined : project.characters[characterConfigIndex]} characterIndex={characterConfigIndex === 'new' ? null : characterConfigIndex} assets={assets} defaultVoice={characterConfigIndex === 'new' ? 'Puck' : project.audioConfig.characters[project.characters[characterConfigIndex].name] || GEMINI_VOICE_OPTIONS[(characterConfigIndex + 1) % GEMINI_VOICE_OPTIONS.length] || 'Puck'} imageEnabled={imageEnabled} audioEnabled={audioEnabled} busy={projectEditBusy} onClose={() => setCharacterConfigIndex(null)} onUploadReference={onUploadReference} onSave={async (draft) => { if (await onSaveCharacter(characterConfigIndex === 'new' ? null : characterConfigIndex, draft)) setCharacterConfigIndex(null); }} onDelete={characterConfigIndex === 'new' || project.characters.length <= 1 ? undefined : async () => { if (await onDeleteCharacter(characterConfigIndex)) setCharacterConfigIndex(null); }} />}
      {rewriteSceneIndex !== null && <RewriteSceneModal scene={project.scenes.find((scene) => scene.index === rewriteSceneIndex) || project.scenes[0]} currentLength={project.input.length} storyLengths={storyLengths} storageKey={`storyweave:scene-edit:${project.id}:${rewriteSceneIndex}`} busy={rewriteBusySceneIndex === rewriteSceneIndex} generationProgress={generationProgress} onClose={() => setRewriteSceneIndex(null)} onSubmit={async (request, onError) => { const succeeded = await onRewrite(rewriteSceneIndex, request, onError); if (succeeded) setRewriteSceneIndex(null); }} />}
      {projectEditorOpen && <ProjectDetailsModal project={project} busy={projectEditBusy} onClose={() => setProjectEditorOpen(false)} onSave={async (draft) => { if (await onSaveProject(draft)) setProjectEditorOpen(false); }} />}
    </section>
  );
}

const customContinuationOptions = [
  '生成结尾',
  '增加自定义剧情',
];

export function ContinuationLengthModal({ currentLength, storyLengths, onClose, onSubmit, onRequireLogin, busy, generationProgress }: { currentLength: StoryLength; storyLengths: StoryLength[]; onClose: () => void; onSubmit: (length: StoryLength, model: TextModelId, onError: ModalErrorSink) => Promise<void>; onRequireLogin?: () => void; busy: boolean; generationProgress?: ClientGenerationTask | null }) {
  const [length, setLength] = useState<StoryLength>(currentLength);
  const [model, setModel] = useState<TextModelId>(DEFAULT_TEXT_MODEL);
  const [error, setError] = useState('');
  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy);
  return createPortal((
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy)) onClose(); }}>
      <section className="continuation-modal" role="dialog" aria-modal="true" aria-labelledby="continuation-length-title">
        <div className="modal-heading"><div><span className="section-kicker">继续生成</span><h2 id="continuation-length-title">选择下一段<br /><em>故事的篇幅。</em></h2></div><button className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">默认沿用当前篇幅；会员可以在续写时调整展开程度。</p>
        <ModalGenerationProgress busy={busy} progress={generationProgress} />
        <div className="modal-input-label">篇幅<StoryLengthSelector value={length} availableLengths={storyLengths} onChange={setLength} name="continuation-length" /></div>
        <div className="modal-input-label">模型<TextModelSelector value={model} onChange={setModel} name="continuation-model" /></div>
        {error && <ModalErrorNotice message={error} onRequireLogin={onRequireLogin} />}
        <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={busy} type="button" onClick={() => { setError(''); void onSubmit(length, model, setError); }}>{busy ? '正在生成…' : '按此篇幅继续 →'}</button></div>
      </section>
    </div>
  ), document.body);
}

export function CustomContinuationModal({ currentLength, storyLengths, onClose, onSubmit, onRequireLogin, busy, generationProgress }: { currentLength: StoryLength; storyLengths: StoryLength[]; onClose: () => void; onSubmit: (instruction: string, length: StoryLength, model: TextModelId, onError: ModalErrorSink) => Promise<void>; onRequireLogin?: () => void; busy: boolean; generationProgress?: ClientGenerationTask | null }) {
  const [selected, setSelected] = useState(customContinuationOptions[1]);
  const [idea, setIdea] = useState('');
  const [length, setLength] = useState<StoryLength>(currentLength);
  const [model, setModel] = useState<TextModelId>(DEFAULT_TEXT_MODEL);
  const [error, setError] = useState('');

  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const instruction = [selected, idea.trim()].filter(Boolean).join('。');
    setError('');
    await onSubmit(instruction, length, model, setError);
  }

  return createPortal((
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy)) onClose(); }}>
      <section className="continuation-modal" role="dialog" aria-modal="true" aria-labelledby="custom-continuation-title">
        <div className="modal-heading"><div><span className="section-kicker">自定义后续</span><h2 id="custom-continuation-title">你想让故事<br /><em>接下来发生什么？</em></h2></div><button className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">选择一个方向，也可以补充你的具体想法。模型会保留现有人物、时间线和悬念逻辑。</p>
        <ModalGenerationProgress busy={busy} progress={generationProgress} />
        {error && <ModalErrorNotice message={error} onRequireLogin={onRequireLogin} />}
        <form onSubmit={submit}>
          <div className="direction-options" role="radiogroup" aria-label="后续方向">
            {customContinuationOptions.map((option) => <label className={selected === option ? 'selected' : ''} key={option}><input checked={selected === option} name="continuation-direction" onChange={() => setSelected(option)} type="radio" />{option}</label>)}
          </div>
          <div className="modal-input-label">篇幅<StoryLengthSelector value={length} availableLengths={storyLengths} onChange={setLength} name="custom-continuation-length" /></div>
          <div className="modal-input-label">模型<TextModelSelector value={model} onChange={setModel} name="custom-continuation-model" /></div>
          <label className="modal-input-label">补充剧情<textarea aria-label="补充剧情" maxLength={1000} value={idea} onChange={(event) => setIdea(event.target.value)} /></label>
          <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={busy} type="submit">{busy ? '正在生成…' : '按这个方向继续 →'}</button></div>
        </form>
      </section>
    </div>
  ), document.body);
}

function DeleteScenesModal({ fromScene, totalScenes, onClose, onConfirm, busy }: { fromScene: number; totalScenes: number; onClose: () => void; onConfirm: () => Promise<void>; busy: boolean }) {
  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy);

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy)) onClose(); }}>
      <section className="continuation-modal delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-scenes-title">
        <div className="modal-heading"><div><span className="section-kicker">删除章节</span><h2 id="delete-scenes-title">删除第 {fromScene} 幕<br /><em>及后续内容？</em></h2></div><button className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">将删除第 {fromScene} 幕到第 {totalScenes} 幕，共 {totalScenes - fromScene + 1} 幕。此操作不可恢复，但前面的章节会保留。</p>
        <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit danger-submit" disabled={busy} type="button" onClick={() => void onConfirm()}>{busy ? '正在删除…' : '确认删除'}</button></div>
      </section>
    </div>,
    document.body,
  );
}

function DeleteProjectModal({ title, onClose, onConfirm, busy }: { title: string; onClose: () => void; onConfirm: () => Promise<void>; busy: boolean }) {
  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy);

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy)) onClose(); }}>
      <section className="continuation-modal delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-project-title">
        <div className="modal-heading"><div><span className="section-kicker">删除小说</span><h2 id="delete-project-title">删除这部小说<br /><em>及全部章节？</em></h2></div><button className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">“{title}”及其全部章节、插图和阅读进度都会被删除。此操作不可恢复。</p>
        <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit danger-submit" disabled={busy} type="button" onClick={() => void onConfirm()}>{busy ? '正在删除…' : '确认删除整本小说'}</button></div>
      </section>
    </div>,
    document.body,
  );
}

const SCENE_EDIT_MODES: Array<{ value: SceneEditRequest['mode']; label: string }> = [
  { value: 'delete', label: '删除段落' },
  { value: 'rewrite', label: '改写选中段落' },
  { value: 'insert', label: '增加段落' },
  { value: 'full_rewrite', label: '自定义重置' },
];

type SceneEditDraft = {
  fingerprint: string;
  mode: SceneEditRequest['mode'];
  selectedParagraphs: number[];
  instruction: string;
  targetCharacters: string;
  model: TextModelId;
  length: StoryLength;
};

function sceneTextFingerprint(text: string) {
  let hash = 2_166_136_261;
  for (const character of text) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16_777_619);
  }
  return `${text.length}:${hash >>> 0}`;
}

function readSceneEditDraft(storageKey: string | undefined, sceneText: string): Partial<SceneEditDraft> {
  if (!storageKey || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const draft = JSON.parse(raw) as Partial<SceneEditDraft>;
    return draft.fingerprint === sceneTextFingerprint(sceneText) ? draft : {};
  } catch {
    return {};
  }
}

function saveSceneEditDraft(storageKey: string | undefined, draft: SceneEditDraft) {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    // Local storage may be disabled; the modal remains fully usable in memory.
  }
}

export function RewriteSceneModal({ scene, currentLength, storyLengths, storageKey, onClose, onSubmit, busy, generationProgress }: { scene: ClientScene; currentLength: StoryLength; storyLengths: StoryLength[]; storageKey?: string; onClose: () => void; onSubmit: (request: SceneEditRequest, onError: ModalErrorSink) => Promise<void>; busy: boolean; generationProgress?: ClientGenerationTask | null }) {
  const [cachedDraft] = useState(() => readSceneEditDraft(storageKey, scene.text));
  const [instruction, setInstruction] = useState(cachedDraft.instruction ?? '');
  const [length, setLength] = useState<StoryLength>(cachedDraft.length && storyLengths.includes(cachedDraft.length) ? cachedDraft.length : currentLength);
  const [model, setModel] = useState<TextModelId>(cachedDraft.model ?? DEFAULT_TEXT_MODEL);
  const [mode, setMode] = useState<SceneEditRequest['mode']>(SCENE_EDIT_MODES.some((item) => item.value === cachedDraft.mode) ? cachedDraft.mode! : 'rewrite');
  const [selectedParagraphs, setSelectedParagraphs] = useState<number[]>(cachedDraft.selectedParagraphs ?? []);
  const [targetCharacters, setTargetCharacters] = useState(cachedDraft.targetCharacters ?? '');
  const [error, setError] = useState('');
  const paragraphs = scene.text.split('\n');

  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy);

  useEffect(() => {
    saveSceneEditDraft(storageKey, {
      fingerprint: sceneTextFingerprint(scene.text),
      mode,
      selectedParagraphs,
      instruction,
      targetCharacters,
      model,
      length,
    });
  }, [instruction, length, mode, model, scene.text, selectedParagraphs, storageKey, targetCharacters]);

  function changeMode(nextMode: SceneEditRequest['mode']) {
    setMode(nextMode);
    setSelectedParagraphs([]);
    setTargetCharacters('');
  }

  function toggleParagraph(index: number) {
    setSelectedParagraphs((current) => current.includes(index)
      ? current.filter((item) => item !== index)
      : [...current, index].sort((left, right) => left - right));
  }

  const adjacentInsertSelection = selectedParagraphs.length === 1
    || (selectedParagraphs.length === 2 && selectedParagraphs[1] === selectedParagraphs[0]! + 1);
  const selectionRequired = mode !== 'full_rewrite';
  const selectionValid = !selectionRequired || (selectedParagraphs.length > 0 && (mode !== 'insert' || adjacentInsertSelection));
  const instructionRequired = mode !== 'delete';
  const parsedTargetCharacters = targetCharacters.trim() ? Number.parseInt(targetCharacters, 10) : undefined;
  const targetCharactersValid = parsedTargetCharacters === undefined || (Number.isSafeInteger(parsedTargetCharacters) && parsedTargetCharacters >= 20 && parsedTargetCharacters <= 4000);
  const canSubmit = (!instructionRequired || Boolean(instruction.trim())) && selectionValid && targetCharactersValid;
  const insertionDescription = mode === 'insert' && adjacentInsertSelection
    ? selectedParagraphs.length === 1
      ? selectedParagraphs[0] === 1 ? '将在第 1 段之前增加' : `将在第 ${selectedParagraphs[0]} 段之后增加`
      : `将在第 ${selectedParagraphs[0]} 段和第 ${selectedParagraphs[1]} 段之间增加`
    : '';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setError('');
    await onSubmit({
      mode,
      selectedParagraphs,
      sceneText: scene.text,
      instruction: instruction.trim() || '删除选中段落',
      ...(parsedTargetCharacters !== undefined && mode !== 'full_rewrite' ? { targetCharacters: parsedTargetCharacters } : {}),
      ...(mode === 'full_rewrite' ? { length } : {}),
      model,
    }, setError);
  }

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy)) onClose(); }}>
      <section className="continuation-modal rewrite-modal" role="dialog" aria-modal="true" aria-labelledby="rewrite-scene-title">
        <div className="modal-heading"><div><span className="section-kicker">编辑第 {String(scene.index).padStart(2, '0')} 幕</span><h2 id="rewrite-scene-title">选择要处理的段落<br /><em>其他剧情保持不变。</em></h2></div><button className="modal-close" disabled={busy} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">删除、改写和增加只作用于你选中的段落；原插图会保留，不会自动重新生成。需要重新创作整幕时，请选择最后一项。</p>
        <ModalGenerationProgress busy={busy} progress={generationProgress} />
        <form onSubmit={submit}>
          <div className="rewrite-original"><span>当前标题</span><strong>{scene.title}</strong><p>{visibleSceneSummary(scene.summary)}</p></div>
          <label className="modal-input-label">编辑方式<select aria-label="段落操作" className="story-length-selector scene-edit-mode-selector" disabled={busy} value={mode} onChange={(event) => changeMode(event.target.value as SceneEditRequest['mode'])}>{SCENE_EDIT_MODES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
          {mode !== 'full_rewrite' && <div className="scene-edit-paragraphs" aria-label="选择段落">
            {paragraphs.map((paragraph, offset) => {
              const index = offset + 1;
              const selected = selectedParagraphs.includes(index);
              return <button className={`scene-edit-paragraph ${selected ? 'selected' : ''}`} disabled={busy} key={index} type="button" onClick={() => toggleParagraph(index)}><span>{String(index).padStart(2, '0')}</span><strong>{Array.from(paragraph.trim()).slice(0, 20).join('') || '（空段落）'}</strong><i>{selected ? '已选择' : '选择'}</i></button>;
            })}
          </div>}
          {insertionDescription && <p className="scene-edit-selection-hint">{insertionDescription}新内容</p>}
          {mode === 'insert' && !adjacentInsertSelection && selectedParagraphs.length > 0 && <p className="scene-edit-selection-error" role="alert">增加段落时请选择一段，或选择相邻的两段。</p>}
          {mode === 'full_rewrite' && <div className="modal-input-label">篇幅<StoryLengthSelector value={length} availableLengths={storyLengths} onChange={setLength} name="rewrite-length" /></div>}
          {mode !== 'delete' && <div className="modal-input-label">模型<TextModelSelector value={model} onChange={setModel} name="rewrite-model" /></div>}
          {(mode === 'rewrite' || mode === 'insert') && <label className="modal-input-label">字数要求<span>可选，留空由模型自行决定</span><input aria-label="字数要求" inputMode="numeric" min="20" max="4000" type="number" value={targetCharacters} onChange={(event) => setTargetCharacters(event.target.value)} placeholder={mode === 'insert' ? '例如 300' : '例如 500'} /></label>}
          {mode !== 'delete' && <label className="modal-input-label">你希望怎么改<textarea aria-label="你希望怎么改" autoFocus maxLength={1200} required value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder={mode === 'insert' ? '例如：承接当前剧情，补充主角发现录音后的追问。' : mode === 'full_rewrite' ? '例如：重写整幕，但保留核心线索。' : '例如：让选中段落的对白更紧张，但不要改变其他剧情。'} /></label>}
          {error && <ModalErrorNotice message={error} />}
          <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={busy || !canSubmit} type="submit">{busy ? '正在处理…' : mode === 'full_rewrite' ? '重置整幕 →' : '执行修改 →'}</button></div>
        </form>
      </section>
    </div>,
    document.body,
  );
}

const MAX_REFERENCE_IMAGE_BYTES = 8 * 1024 * 1024;
const REFERENCE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function ImageGenerationModal({ projectId, scene, assets, onClose, onUploadReference, onSubmit, onBrowseHistory, historyCount, busy, generationProgress }: { projectId: string; scene: ClientScene; assets: ClientReferenceAsset[]; onClose: () => void; onUploadReference: (dataUrl: string, label: string) => Promise<ClientReferenceAsset>; onSubmit: (source: SceneImageSource, model: ImageModelId, customPrompt?: string, referenceImageDataUrl?: string, referenceAssetIds?: string[]) => Promise<ImageGenerationErrorResponse | null>; onBrowseHistory: () => void; historyCount: number; busy: boolean; generationProgress?: ClientGenerationTask | null }) {
  const [source, setSource] = useState<SceneImageSource>('prompt');
  const [model, setModel] = useState<ImageModelId>(IMAGE_MODEL_LITE);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [pendingReference, setPendingReference] = useState<{ name: string; dataUrl: string }>();
  const [referenceError, setReferenceError] = useState('');
  const [imageFailure, setImageFailure] = useState<ImageGenerationErrorResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const locked = busy || submitting;

  useBodyScrollLock(true);
  useEscapeToClose(onClose, locked);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setReferenceError('');
    setImageFailure(null);
    setSubmitting(true);
    try {
      let ids = selectedAssetIds;
      if (pendingReference) {
        const uploaded = await onUploadReference(pendingReference.dataUrl, pendingReference.name);
        ids = [...new Set([...ids, uploaded.id])];
      }
      const failure = await onSubmit(source, model, ['custom', 'scene-edit'].includes(source) ? customPrompt.trim() : undefined, undefined, ids);
      if (failure) setImageFailure(failure);
    } catch (cause) {
      setReferenceError(cause instanceof Error ? cause.message : '参考图处理失败，请重试。');
    } finally {
      setSubmitting(false);
    }
  }

  async function chooseReferenceImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    setReferenceError('');
    if (!file) return;
    if (!REFERENCE_IMAGE_TYPES.includes(file.type)) {
      setReferenceError('请上传 PNG、JPEG 或 WebP 图片。');
      return;
    }
    if (file.size > MAX_REFERENCE_IMAGE_BYTES) {
      setReferenceError('参考图不能超过 8MB。');
      return;
    }
    try {
      setPendingReference({ name: file.name, dataUrl: await readReferenceFile(file) });
    } catch (cause) {
      setReferenceError(cause instanceof Error ? cause.message : '参考图读取失败，请重试。');
    }
  }

  return createPortal((
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, locked)) onClose(); }}>
      <section className="continuation-modal image-generation-modal" role="dialog" aria-modal="true" aria-labelledby="image-generation-title">
        <div className="modal-heading"><div><span className="section-kicker">第 {String(scene.index).padStart(2, '0')} 幕 / 插图</span><h2 id="image-generation-title">为这一幕<br /><em>织入一张画面。</em></h2></div><button className="modal-close" disabled={locked} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">只生成当前这一幕。选择素材来源，模型会保留小说人物、地点和情节上下文。</p>
        <ModalGenerationProgress busy={locked} progress={generationProgress} />
        {imageFailure && <div className="image-generation-error" role="alert"><div className="image-generation-error-heading"><strong>{toImageErrorPanel(imageFailure).title}</strong><span>{imageFailure.code}</span></div><p>{toImageErrorPanel(imageFailure).message}</p><p><b>建议：</b>{toImageErrorPanel(imageFailure).suggestion}</p><code>请求编号：{imageFailure.requestId}</code></div>}
        <form onSubmit={submit}>
          <div className="direction-options image-source-options" role="radiogroup" aria-label="图片素材来源">
            <label className={source === 'prompt' ? 'selected' : ''}><input checked={source === 'prompt'} name="image-source" onChange={() => setSource('prompt')} type="radio" />使用本幕场景提示词</label>
            <label className={source === 'excerpt' ? 'selected' : ''}><input checked={source === 'excerpt'} name="image-source" onChange={() => setSource('excerpt')} type="radio" />使用本幕正文节选</label>
            <label className={source === 'scene-edit' ? 'selected' : ''}><input checked={source === 'scene-edit'} name="image-source" onChange={() => setSource('scene-edit')} type="radio" />基于本幕修改画面</label>
            <label className={source === 'custom' ? 'selected' : ''}><input checked={source === 'custom'} name="image-source" onChange={() => setSource('custom')} type="radio" />完全自定义画面</label>
          </div>
          {['custom', 'scene-edit'].includes(source) && <label className="modal-input-label">画面提示<textarea aria-label="自定义画面提示" maxLength={500} required value={customPrompt} onChange={(event) => setCustomPrompt(event.target.value)} placeholder={source === 'custom' ? '只描述你希望看到的画面。' : '例如：改成夜晚，保留人物和当前地点。'} /></label>}
          <div className="reference-image-section">
            <span className="modal-input-label">项目参考图 <span>可多选</span></span>
            <ReferenceAssetGrid assets={assets} selectedIds={selectedAssetIds} onToggle={(assetId) => setSelectedAssetIds((current) => current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId])} />
            {pendingReference ? <div className="reference-image-preview"><img alt="待上传的参考图" src={pendingReference.dataUrl} /><div><strong>{pendingReference.name}</strong><button disabled={locked} type="button" onClick={() => setPendingReference(undefined)}>移除</button></div></div> : <label className="reference-image-upload"><input accept="image/png,image/jpeg,image/webp" disabled={locked} onChange={chooseReferenceImage} type="file" /><span>＋</span><strong>上传并保存一张新参考图</strong><small>PNG、JPEG 或 WebP，最大 8MB；相同图片自动去重</small></label>}
            {referenceError && <p className="reference-image-error" role="alert">{referenceError}</p>}
            <p className="reference-image-hint">已配置的角色参考图会由服务端自动带入；这里可以额外选择人物、服装或背景参考。</p>
          </div>
          <div className="image-model-section">
            <span className="modal-input-label">生成模型</span>
            <div className="direction-options image-model-options" role="radiogroup" aria-label="图片生成模型">
              {IMAGE_MODEL_OPTIONS.map((option) => <label className={model === option.id ? 'selected' : ''} key={option.id}><input checked={model === option.id} name="image-model" onChange={() => setModel(option.id)} type="radio" /><span className="image-model-copy"><strong>{option.label}</strong><small>{option.description}</small></span></label>)}
            </div>
          </div>
          <div className="image-generation-bottomline"><p className="image-generation-note">将生成 1 张图片，可能消耗图片模型额度。</p><button className="scene-history-link" disabled={locked} type="button" onClick={onBrowseHistory}>浏览历史图片{historyCount > 0 ? ` · ${historyCount}` : ''}</button></div>
          <div className="modal-actions"><button className="modal-cancel" disabled={locked} type="button" onClick={onClose}>取消</button><button className="modal-submit" disabled={locked} type="submit">{locked ? '正在准备并生成…' : '确认生成 →'}</button></div>
        </form>
      </section>
    </div>
  ), document.body);
}

function useCachedImage(url: string) {
  const [source, setSource] = useState('');

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setSource('');

    void loadCachedImage(url).then((result) => {
      if (!active) {
        if (result.objectUrl) URL.revokeObjectURL(result.src);
        return;
      }
      objectUrl = result.objectUrl ? result.src : '';
      setSource(result.src);
    });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return source;
}

function SceneArt({ projectId, scene, refreshKey, busy, onGenerate, onOpen }: { projectId: string; scene: ClientScene; refreshKey: string; busy: boolean; onGenerate: () => void; onOpen: () => void }) {
  const imageUrl = getSceneImageUrl(projectId, scene.index, refreshKey);
  const cachedImageUrl = useCachedImage(imageUrl);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => setStatus('loading'), [imageUrl]);
  useEffect(() => {
    if (!cachedImageUrl) setStatus('missing');
  }, [cachedImageUrl]);

  return (
    <div className="scene-art-panel">
      <div className="scene-art-frame">
        <img alt={`${scene.title}插图`} className={`scene-image ${status === 'ready' ? '' : 'is-hidden'}`} src={cachedImageUrl || undefined} onError={() => setStatus('missing')} onLoad={() => setStatus('ready')} />
        {status !== 'ready' && <button className="art-placeholder art-placeholder-button" disabled={busy} type="button" onClick={onGenerate}><span>第 {String(scene.index).padStart(2, '0')} 幕</span><strong>{busy ? <>正在生成<br />请稍候</> : status === 'loading' ? <>正在读取<br />插图状态</> : <>点击生成<br />本幕插图</>}</strong><small>{busy ? '图片生成完成后会自动保存。' : scene.imagePrompt || '当前还没有生成图片。'}</small></button>}
        {status === 'ready' && <button aria-label={`查看${scene.title}大图`} className="scene-image-hit-area" type="button" onClick={onOpen} />}
      </div>
      <div className="scene-art-footer"><span>{status === 'ready' ? '已生成本幕插图' : '图片默认不生成'}</span><button disabled={busy || status === 'loading'} type="button" onClick={onGenerate}>{status === 'ready' ? '更换图片' : '生成插图'}</button></div>
    </div>
  );
}

function ImageLightbox({ projectId, scene, cacheKey, onClose, onReplace }: { projectId: string; scene: ClientScene; cacheKey: string; onClose: () => void; onReplace: () => void }) {
  const cachedImageUrl = useCachedImage(getSceneImageUrl(projectId, scene.index, cacheKey));
  useBodyScrollLock(true);
  useEscapeToClose(onClose, false);

  return createPortal((
    <div className="image-lightbox" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, false)) onClose(); }}>
      <button aria-label="关闭大图" className="lightbox-close" type="button" onClick={onClose}>×</button>
      <img alt={`${scene.title}大图`} src={cachedImageUrl || undefined} />
      <div className="lightbox-actions"><span>第 {scene.index} 幕 · {scene.title}</span><button type="button" onClick={onReplace}>更换图片</button></div>
    </div>
  ), document.body);
}

function SceneImageHistoryThumbnail({ projectId, scene, asset, onOpen }: { projectId: string; scene: ClientScene; asset: SceneImage; onOpen: () => void }) {
  const cachedImageUrl = useCachedImage(getSceneImageVersionUrl(projectId, scene.index, asset.id));
  return (
    <button className={`scene-history-item${asset.isPrimary ? ' is-primary' : ''}`} type="button" onClick={onOpen} aria-label={`${scene.title}，${asset.isPrimary ? '当前主图' : '历史版本'}，${formatDate(asset.createdAt)}`}>
      <span className="scene-history-image-wrap">{cachedImageUrl ? <img alt="" src={cachedImageUrl} /> : <i aria-hidden="true">读取中…</i>}{asset.isPrimary && <b>主图</b>}</span>
      <span className="scene-history-item-meta"><strong>{asset.isPrimary ? '当前主图' : '历史版本'}</strong><small>{formatDate(asset.createdAt)}</small></span>
    </button>
  );
}

function SceneImageHistoryLightbox({ projectId, scene, asset, busy, onClose, onSetPrimary, onDelete }: { projectId: string; scene: ClientScene; asset: SceneImage; busy: boolean; onClose: () => void; onSetPrimary: () => void; onDelete: () => void }) {
  const cachedImageUrl = useCachedImage(getSceneImageVersionUrl(projectId, scene.index, asset.id));
  useBodyScrollLock(true);
  useEscapeToClose(onClose, busy);

  return createPortal((
    <div className="image-lightbox scene-history-lightbox" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busy)) onClose(); }}>
      <button aria-label="关闭历史图片大图" className="lightbox-close" disabled={busy} type="button" onClick={onClose}>×</button>
      {cachedImageUrl ? <img alt={`${scene.title}历史图片`} src={cachedImageUrl} /> : <span className="scene-history-lightbox-loading">正在读取图片…</span>}
      <div className="lightbox-actions scene-history-lightbox-actions"><span>第 {scene.index} 幕 · {formatDate(asset.createdAt)}{asset.isPrimary ? ' · 当前主图' : ''}</span><div><button disabled={busy || asset.isPrimary} type="button" onClick={onSetPrimary}>{asset.isPrimary ? '当前主图' : '设为主图'}</button><button className="lightbox-delete" disabled={busy} type="button" onClick={onDelete}>删除此版本</button></div></div>
    </div>
  ), document.body);
}

export function SceneImageHistoryModal({ projectId, scene, assets, requestScope, operationController, onClose, onAssetsChange, onPrimaryChanged, onProjectNotFound }: { projectId: string; scene: ClientScene; assets: SceneImage[]; requestScope: ChildProjectScope; operationController?: OperationController; onClose: () => void; onAssetsChange: (assets: SceneImage[]) => void; onPrimaryChanged: (asset: SceneImage | null) => Promise<void>; onProjectNotFound: ProjectNotFoundHandler }) {
  const [items, setItems] = useState<SceneImage[]>(assets);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyAssetId, setBusyAssetId] = useState<string | null>(null);
  const localOperations = useRef(new OperationController());
  const operations = operationController ?? localOperations.current;
  const listAbort = useRef<AbortController | undefined>(undefined);
  const mutationAbort = useRef<AbortController | undefined>(undefined);
  const mounted = useRef(false);
  const callbacks = useRef({ onAssetsChange, onPrimaryChanged, onProjectNotFound });
  callbacks.current = { onAssetsChange, onPrimaryChanged, onProjectNotFound };
  const selected = selectedId ? items.find((item) => item.id === selectedId) ?? null : null;

  useBodyScrollLock(true);
  useEscapeToClose(onClose, busyAssetId !== null);

  useEffect(() => {
    setItems(assets);
  }, [assets]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => () => {
    operations.invalidate();
    listAbort.current?.abort();
    listAbort.current = undefined;
    mutationAbort.current?.abort();
    mutationAbort.current = undefined;
  }, [projectId, requestScope, scene.index]);

  useEffect(() => {
    listAbort.current?.abort();
    const operation = operations.begin();
    const controller = new AbortController();
    listAbort.current = controller;
    setLoading(true);
    setError('');
    void listSceneImages(projectId, scene.index, controller.signal).then((nextItems) => {
      if (!mounted.current || !operations.isCurrent(operation)) return;
      setItems(nextItems);
      callbacks.current.onAssetsChange(nextItems);
    }).catch((cause) => {
      if (!mounted.current || !operations.isCurrent(operation)) return;
      if (isProjectNotFoundError(cause)) {
        callbacks.current.onProjectNotFound(requestScope, '这部小说已经不存在，无法读取历史图片。');
      } else {
        setError(cause instanceof Error ? cause.message : '历史图片读取失败，请重试。');
      }
    }).finally(() => {
      if (listAbort.current === controller) listAbort.current = undefined;
      if (mounted.current && operations.isCurrent(operation)) setLoading(false);
    });
    return () => {
      operations.invalidate();
      controller.abort();
      if (listAbort.current === controller) listAbort.current = undefined;
    };
  }, [projectId, requestScope, scene.index]);

  async function handleSetPrimary(asset: SceneImage) {
    if (asset.isPrimary || busyAssetId) return;
    listAbort.current?.abort();
    listAbort.current = undefined;
    mutationAbort.current?.abort();
    const operation = operations.beginBusy();
    const controller = new AbortController();
    mutationAbort.current = controller;
    setLoading(false);
    setBusyAssetId(asset.id);
    setError('');
    try {
      const updated = await setSceneImagePrimary(projectId, scene.index, asset.id, controller.signal);
      if (!mounted.current || !operations.isCurrent(operation)) return;
      const nextItems = items.map((item) => ({ ...item, isPrimary: item.id === updated.id }));
      setItems(nextItems);
      callbacks.current.onAssetsChange(nextItems);
      await callbacks.current.onPrimaryChanged(updated);
    } catch (cause) {
      if (!mounted.current || !operations.isCurrent(operation) || isAbortError(cause)) return;
      if (isProjectNotFoundError(cause)) {
        callbacks.current.onProjectNotFound(requestScope, '这部小说已经不存在，无法切换主图。');
      } else {
        setError(cause instanceof Error ? cause.message : '主图切换失败，请重试。');
      }
    } finally {
      if (mutationAbort.current === controller) mutationAbort.current = undefined;
      const ownedBusy = operations.finishBusy(operation);
      if (mounted.current && ownedBusy) setBusyAssetId(null);
    }
  }

  async function handleDelete(asset: SceneImage) {
    if (busyAssetId || !window.confirm('删除这张历史图片？删除后不可恢复。')) return;
    listAbort.current?.abort();
    listAbort.current = undefined;
    mutationAbort.current?.abort();
    const operation = operations.beginBusy();
    const controller = new AbortController();
    mutationAbort.current = controller;
    setLoading(false);
    setBusyAssetId(asset.id);
    setError('');
    try {
      const nextPrimary = await deleteSceneImage(projectId, scene.index, asset.id, controller.signal);
      if (!mounted.current || !operations.isCurrent(operation)) return;
      const nextItems = applySceneImageMutation({ assets: items, mutation: { type: 'delete', deletedId: asset.id, nextPrimaryId: nextPrimary?.id ?? null } }).assets;
      setItems(nextItems);
      callbacks.current.onAssetsChange(nextItems);
      setSelectedId((current) => current === asset.id ? null : current);
      if (asset.isPrimary) await callbacks.current.onPrimaryChanged(nextPrimary);
    } catch (cause) {
      if (!mounted.current || !operations.isCurrent(operation) || isAbortError(cause)) return;
      if (isProjectNotFoundError(cause)) {
        callbacks.current.onProjectNotFound(requestScope, '这部小说已经不存在，无法删除历史图片。');
      } else {
        setError(cause instanceof Error ? cause.message : '历史图片删除失败，请重试。');
      }
    } finally {
      if (mutationAbort.current === controller) mutationAbort.current = undefined;
      const ownedBusy = operations.finishBusy(operation);
      if (mounted.current && ownedBusy) setBusyAssetId(null);
    }
  }

  return createPortal((
    <div className="modal-backdrop scene-history-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, busyAssetId !== null)) onClose(); }}>
      <section className="continuation-modal scene-history-modal" role="dialog" aria-modal="true" aria-labelledby="scene-history-title">
        <div className="modal-heading"><div><span className="section-kicker">第 {String(scene.index).padStart(2, '0')} 幕 / 历史图片</span><h2 id="scene-history-title">这一幕的<br /><em>所有画面。</em></h2></div><button className="modal-close" disabled={busyAssetId !== null} type="button" onClick={onClose}>×</button></div>
        <p className="scene-history-summary">{scene.title} · {loading ? '正在读取…' : `${items.length} 个版本`}<br /><span>点击任意缩略图查看大图，并可设置当前主图。</span></p>
        {error && <p className="scene-history-error" role="alert">{error}</p>}
        {loading && items.length === 0 && <div className="scene-history-empty">正在读取历史图片…</div>}
        {!loading && items.length === 0 && <div className="scene-history-empty">这一幕还没有历史图片。生成后的每个版本都会保存在这里。</div>}
        {items.length > 0 && <div className="scene-history-grid">{items.map((asset) => <SceneImageHistoryThumbnail key={asset.id} projectId={projectId} scene={scene} asset={asset} onOpen={() => setSelectedId(asset.id)} />)}</div>}
        <div className="modal-actions"><button className="modal-cancel" disabled={busyAssetId !== null} type="button" onClick={onClose}>返回阅读</button></div>
      </section>
      {selected && <SceneImageHistoryLightbox projectId={projectId} scene={scene} asset={selected} busy={busyAssetId === selected.id} onClose={() => setSelectedId(null)} onSetPrimary={() => void handleSetPrimary(selected)} onDelete={() => void handleDelete(selected)} />}
    </div>
  ), document.body);
}

export function ReaderView({ project, features, storyLengths = ['short'], assets, requestScope, initialSceneIndex, onBack, onContinue, onRequireLogin, onDeleteFrom, onUploadReference, onGenerateImage, onRewrite, onSceneChange, onProjectNotFound, continuing, deleting, imageBusy, rewriteBusySceneIndex, generationProgress }: { project: ClientProject; features: EffectiveFeatures; storyLengths?: StoryLength[]; assets: ClientReferenceAsset[]; requestScope: ChildProjectScope; initialSceneIndex?: number; onBack: () => void; onContinue: ContinueHandler; onRequireLogin?: () => void; onDeleteFrom: DeleteHandler; onUploadReference: (dataUrl: string, label: string) => Promise<ClientReferenceAsset>; onGenerateImage: GenerateImageHandler; onRewrite: RewriteHandler; onSceneChange: (sceneIndex: number) => void; onProjectNotFound: ProjectNotFoundHandler; continuing: boolean; deleting: boolean; imageBusy: boolean; rewriteBusySceneIndex: number | null; generationProgress?: ClientGenerationTask | null }) {
  const imageEnabled = features.image.enabled;
  const audioEnabled = features.audio.enabled;
  const [activeIndex, setActiveIndex] = useState(() => resolveReaderStartIndex(initialSceneIndex, readReaderPosition(project.id), project.scenes));
  const [knownSceneCount, setKnownSceneCount] = useState(project.scenes.length);
  const [customContinuationOpen, setCustomContinuationOpen] = useState(false);
  const [continuationLengthOpen, setContinuationLengthOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<SceneImage[]>([]);
  const [rewriteModalOpen, setRewriteModalOpen] = useState(false);
  const [imageRefreshKey, setImageRefreshKey] = useState('initial');
  const historyOperations = useRef(new OperationController());
  const historyAbort = useRef<AbortController | undefined>(undefined);
  const imageGenerationAbort = useRef<AbortController | undefined>(undefined);
  const imageRefreshAbort = useRef<AbortController | undefined>(undefined);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const continuationStart = getContinuationStartIndex(knownSceneCount, project.scenes.length);
    if (continuationStart !== null) {
      setActiveIndex(continuationStart);
      setKnownSceneCount(project.scenes.length);
    }
  }, [knownSceneCount, project.scenes.length]);

  useEffect(() => {
    const scene = project.scenes[activeIndex];
    if (scene) {
      saveReaderPosition(project.id, scene.index);
      onSceneChange(scene.index);
    }
  }, [activeIndex, project.id, project.scenes, onSceneChange]);

  useEffect(() => {
    if (project.scenes.length > 0 && activeIndex >= project.scenes.length) {
      setActiveIndex(project.scenes.length - 1);
    }
  }, [activeIndex, project.scenes.length]);

  const activeSceneIndex = project.scenes[activeIndex]?.index;
  useEffect(() => {
    historyAbort.current?.abort();
    historyAbort.current = undefined;
    if (!imageEnabled || activeSceneIndex === undefined) {
      historyOperations.current.invalidate();
      setHistoryItems([]);
      return;
    }
    const operation = historyOperations.current.begin();
    const controller = new AbortController();
    historyAbort.current = controller;
    void listSceneImages(project.id, activeSceneIndex, controller.signal).then((items) => {
      if (mounted.current && historyOperations.current.isCurrent(operation)) setHistoryItems(items);
    }).catch((cause) => {
      if (!mounted.current || !historyOperations.current.isCurrent(operation)) return;
      if (isAbortError(cause)) return;
      if (isProjectNotFoundError(cause)) {
        onProjectNotFound(requestScope, '这部小说已经不存在，无法读取场景图片。');
      } else {
        setHistoryItems([]);
      }
    });
    return () => {
      historyOperations.current.invalidate();
      controller.abort();
      if (historyAbort.current === controller) historyAbort.current = undefined;
    };
  }, [activeSceneIndex, imageEnabled, project.id, requestScope]);

  useEffect(() => () => {
    historyOperations.current.invalidate();
    historyAbort.current?.abort();
    historyAbort.current = undefined;
    imageGenerationAbort.current?.abort();
    imageGenerationAbort.current = undefined;
    imageRefreshAbort.current?.abort();
    imageRefreshAbort.current = undefined;
  }, [activeSceneIndex, project.id, requestScope]);

  const scene = project.scenes[activeIndex];
  if (!scene) {
    return <section className="workspace reader-view reader-empty"><p>这部小说暂时没有可阅读的内容。</p><button type="button" onClick={onBack}>← 返回大纲</button></section>;
  }
  function goToIndex(index: number) {
    if (index >= 0 && index < project.scenes.length) {
      setActiveIndex(index);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }
  async function submitImage(source: SceneImageSource, model: ImageModelId, customPrompt?: string, referenceImageDataUrl?: string, referenceAssetIds?: string[]) {
    if (!imageEnabled) return null;
    historyAbort.current?.abort();
    historyAbort.current = undefined;
    imageGenerationAbort.current?.abort();
    imageRefreshAbort.current?.abort();
    const operation = historyOperations.current.begin();
    const generationController = new AbortController();
    imageGenerationAbort.current = generationController;
    const isCurrent = () => mounted.current && historyOperations.current.isCurrent(operation);
    const outcome = await onGenerateImage(requestScope, scene.index, source, model, customPrompt, referenceImageDataUrl, referenceAssetIds, generationController.signal);
    if (!isCurrent()) return null;
    if (imageGenerationAbort.current === generationController) imageGenerationAbort.current = undefined;
    if (outcome === undefined) return null;
    const failure = outcome;
    if (!failure) {
      if (!isCurrent()) return null;
      await invalidateCachedImage(getSceneImageUrl(project.id, scene.index));
      if (!isCurrent()) return null;
      let items: SceneImage[];
      const controller = new AbortController();
      imageRefreshAbort.current = controller;
      try {
        items = await listSceneImages(project.id, scene.index, controller.signal);
      } catch (cause) {
        if (!isCurrent() || isAbortError(cause)) return null;
        if (isProjectNotFoundError(cause)) {
          onProjectNotFound(requestScope, '这部小说已经不存在，无法刷新场景图片。');
          return null;
        }
        items = [];
      }
      if (!isCurrent()) return null;
      if (imageRefreshAbort.current === controller) imageRefreshAbort.current = undefined;
      setHistoryItems(items);
      setImageModalOpen(false);
      setImageRefreshKey(String(Date.now()));
    }
    return failure;
  }
  return (
    <section className="workspace reader-view">
      <header className="workspace-header"><button className="back-button" type="button" onClick={onBack}>← 大纲</button><div className="reader-controls"><span className="reader-count">{String(activeIndex + 1).padStart(2, '0')} / {String(project.scenes.length).padStart(2, '0')}</span><select aria-label="跳转到场景" key={`${activeIndex}-${project.scenes.length}`} defaultValue="" onChange={(event) => { if (event.target.value) goToIndex(Number(event.target.value)); }}><option value="">跳转到…</option>{project.scenes.map((item, index) => <option key={item.index} value={index}>第 {index + 1} 幕 · {item.title}</option>)}</select><button className="danger-link" disabled={deleting} type="button" onClick={() => setPendingDelete(scene.index)}>删除本幕及后续</button></div></header>
      <div className={`reader-layout reader-layout--${imageEnabled ? 'with-art' : 'text-only'}`} data-testid="reader-layout">
        <div className="reader-text">
          <div className="reader-intro"><span className="section-kicker">{project.title}</span><div className="reader-scene-heading"><h1>{scene.title}</h1><button className="scene-rewrite-button" disabled={rewriteBusySceneIndex !== null} type="button" onClick={() => setRewriteModalOpen(true)}>{rewriteBusySceneIndex === scene.index ? '正在重置…' : '重置本幕'}</button></div><p className="scene-summary">{visibleSceneSummary(scene.summary)}</p></div>
          {imageEnabled && <div aria-label="本幕插图" className="reader-art"><SceneArt projectId={project.id} scene={scene} refreshKey={imageRefreshKey} busy={imageBusy} onGenerate={() => setImageModalOpen(true)} onOpen={() => setLightboxOpen(true)} /></div>}
          <div className="reader-copy"><div className="body-copy">{scene.text.split('\n').map((paragraph, index) => <p key={`${index}-${paragraph}`}>{paragraph}</p>)}</div>{audioEnabled && <SceneAudioPanel projectId={project.id} sceneIndex={scene.index} requestScope={requestScope} onProjectNotFound={onProjectNotFound} />}<div className="reader-nav"><button disabled={activeIndex === 0} type="button" onClick={() => goToIndex(activeIndex - 1)}>← 上一幕</button>{activeIndex === project.scenes.length - 1 ? <button className="continue-reading" disabled={continuing} type="button" onClick={() => setContinuationLengthOpen(true)}>{continuing ? '正在续写…' : '继续生成后续 →'}</button> : <button type="button" onClick={() => goToIndex(activeIndex + 1)}>下一幕 →</button>}</div>{activeIndex === project.scenes.length - 1 && <p className="continuation-hint">这一幕是当前故事的结尾。继续生成后，会自动打开新章节。<button className="custom-continuation-link" disabled={continuing} type="button" onClick={() => setCustomContinuationOpen(true)}>自定义后续</button></p>}</div>
        </div>
      </div>
      {continuationLengthOpen && <ContinuationLengthModal currentLength={project.input.length} storyLengths={storyLengths} busy={continuing} generationProgress={generationProgress} onRequireLogin={onRequireLogin} onClose={() => setContinuationLengthOpen(false)} onSubmit={async (length, model, onError) => { const succeeded = await onContinue(undefined, length, model, onError); if (succeeded) setContinuationLengthOpen(false); }} />}
      {customContinuationOpen && <CustomContinuationModal currentLength={project.input.length} storyLengths={storyLengths} busy={continuing} generationProgress={generationProgress} onRequireLogin={onRequireLogin} onClose={() => setCustomContinuationOpen(false)} onSubmit={async (instruction, length, model, onError) => { const succeeded = await onContinue(instruction, length, model, onError); if (succeeded) setCustomContinuationOpen(false); }} />}
      {pendingDelete !== null && <DeleteScenesModal fromScene={pendingDelete} totalScenes={project.scenes.length} busy={deleting} onClose={() => setPendingDelete(null)} onConfirm={async () => { if (await onDeleteFrom(pendingDelete)) setPendingDelete(null); }} />}
      {rewriteModalOpen && <RewriteSceneModal scene={scene} currentLength={project.input.length} storyLengths={storyLengths} storageKey={`storyweave:scene-edit:${project.id}:${scene.index}`} busy={rewriteBusySceneIndex === scene.index} generationProgress={generationProgress} onClose={() => setRewriteModalOpen(false)} onSubmit={async (request, onError) => { const succeeded = await onRewrite(scene.index, request, onError); if (succeeded) setRewriteModalOpen(false); }} />}
      {imageEnabled && imageModalOpen && <ImageGenerationModal projectId={project.id} scene={scene} assets={assets} onUploadReference={onUploadReference} busy={imageBusy} generationProgress={generationProgress} historyCount={historyItems.length} onBrowseHistory={() => { setImageModalOpen(false); setHistoryOpen(true); }} onClose={() => setImageModalOpen(false)} onSubmit={submitImage} />}
      {imageEnabled && historyOpen && <SceneImageHistoryModal projectId={project.id} scene={scene} assets={historyItems} requestScope={requestScope} operationController={historyOperations.current} onClose={() => setHistoryOpen(false)} onAssetsChange={(items) => { if (mounted.current) setHistoryItems(items); }} onPrimaryChanged={async () => { historyAbort.current?.abort(); historyAbort.current = undefined; const operation = historyOperations.current.begin(); await invalidateCachedImage(getSceneImageUrl(project.id, scene.index)); if (mounted.current && historyOperations.current.isCurrent(operation)) setImageRefreshKey(String(Date.now())); }} onProjectNotFound={onProjectNotFound} />}
      {imageEnabled && lightboxOpen && <ImageLightbox projectId={project.id} scene={scene} cacheKey={imageRefreshKey} onClose={() => setLightboxOpen(false)} onReplace={() => { setLightboxOpen(false); setImageModalOpen(true); }} />}
    </section>
  );
}

export default function App() {
  const [projects, setProjects] = useState<ClientProjectSummary[]>([]);
  const [current, setCurrent] = useState<ClientProject>();
  const [route, setRoute] = useState<AppRoute>(() => parseRoute(window.location.pathname));
  const routeProjectId = projectIdFromRoute(route);
  const [continuing, setContinuing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [projectEditBusy, setProjectEditBusy] = useState(false);
  const [imageBusySceneIndex, setImageBusySceneIndex] = useState<number | null>(null);
  const [rewriteBusySceneIndex, setRewriteBusySceneIndex] = useState<number | null>(null);
  const [referenceAssets, setReferenceAssets] = useState<ClientReferenceAsset[]>([]);
  const [projectListLoading, setProjectListLoading] = useState(true);
  const [projectListError, setProjectListError] = useState('');
  const [projectListRetryIntent, setProjectListRetryIntent] = useState<ProjectListRetryIntent>();
  const [nextProjectCursor, setNextProjectCursor] = useState<string>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [projectDetailLoading, setProjectDetailLoading] = useState(false);
  const [projectDetailError, setProjectDetailError] = useState('');
  const [projectDetailRetryNonce, setProjectDetailRetryNonce] = useState(0);
  const [error, setError] = useState('');
  const [authConfig, setAuthConfig] = useState<AuthConfig>({ phoneLogin: false, turnstileSiteKey: '', turnstileRequired: false });
  const [account, setAccount] = useState<AuthMe>(DEFAULT_GUEST_ACCOUNT);
  const [identityReady, setIdentityReady] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [projectExport, setProjectExport] = useState<ClientProject>();
  const [projectImportOpen, setProjectImportOpen] = useState(false);
  const [billingPage, setBillingPage] = useState<UsagePage>();
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLoadingMore, setBillingLoadingMore] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [generationProgress, setGenerationProgress] = useState<ClientGenerationTask | null>(null);
  const requestGate = useRef(new ProjectRequestGate());
  const responseCoordinator = useRef(new ProjectResponseCoordinator());
  const imageBusyOperations = useRef(new OperationController());
  const firstProjectPageAbort = useRef<AbortController | undefined>(undefined);
  const appendProjectsAbort = useRef<AbortController | undefined>(undefined);
  const selectionAbort = useRef<AbortController | undefined>(undefined);
  const referenceAssetsAbort = useRef<AbortController | undefined>(undefined);
  const projectRecoveryAborts = useRef(new Map<string, AbortController>());
  const billingAbort = useRef<AbortController | undefined>(undefined);
  const detailCache = useRef(new Map<string, ClientProject>());
  const activeRouteProjectId = useRef(routeProjectId);
  activeRouteProjectId.current = routeProjectId;
  const generationProgressInModal = continuing || rewriteBusySceneIndex !== null || imageBusySceneIndex !== null;

  function navigate(nextRoute: AppRoute, replace = false) {
    const nextPath = routePath(nextRoute);
    if (window.location.pathname === nextPath && routePath(route) === nextPath) return;
    if (window.location.pathname !== nextPath) {
      window.history[replace ? 'replaceState' : 'pushState']({}, '', nextPath);
    }
    activeRouteProjectId.current = projectIdFromRoute(nextRoute);
    setRoute(nextRoute);
  }

  function captureIdentity() {
    return requestGate.current.captureIdentity();
  }

  function isIdentityCurrent(originEpoch: number) {
    return requestGate.current.isIdentityCurrent(originEpoch);
  }

  function invalidateProjectListRequest() {
    requestGate.current.invalidateProjectLists();
    firstProjectPageAbort.current?.abort();
    appendProjectsAbort.current?.abort();
    firstProjectPageAbort.current = undefined;
    appendProjectsAbort.current = undefined;
    setProjectListLoading(false);
    setLoadingMore(false);
  }

  function advanceAccountIdentity() {
    requestGate.current.advanceIdentity();
    responseCoordinator.current.clearAccount();
    firstProjectPageAbort.current?.abort();
    appendProjectsAbort.current?.abort();
    selectionAbort.current?.abort();
    referenceAssetsAbort.current?.abort();
    billingAbort.current?.abort();
    abortProjectRecoveries();
    firstProjectPageAbort.current = undefined;
    appendProjectsAbort.current = undefined;
    selectionAbort.current = undefined;
    referenceAssetsAbort.current = undefined;
    billingAbort.current = undefined;
  }

  function cancelProjectRecovery(projectId: string) {
    requestGate.current.invalidateProjectRecovery(projectId);
    projectRecoveryAborts.current.get(projectId)?.abort();
    projectRecoveryAborts.current.delete(projectId);
  }

  function abortProjectRecoveries() {
    for (const controller of projectRecoveryAborts.current.values()) controller.abort();
    projectRecoveryAborts.current.clear();
  }

  useEffect(() => {
    const handlePopState = () => {
      const nextRoute = parseRoute(window.location.pathname);
      activeRouteProjectId.current = projectIdFromRoute(nextRoute);
      setRoute(nextRoute);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const billingIdentity = account.authenticated ? account.maskedPhone : 'guest';
  const displayFeatures = visibleFeatures(account);
  const canTransfer = account.authenticated && canUseProjectTransfer(account);
  useEffect(() => {
    billingAbort.current?.abort();
    billingAbort.current = undefined;
    if (route.view !== 'billing' || !identityReady) return;
    if (!account.authenticated) {
      setBillingPage(undefined);
      setBillingLoading(false);
      setBillingLoadingMore(false);
      setBillingError('请先登录后查看消费记录。');
      return;
    }
    const abortController = new AbortController();
    billingAbort.current = abortController;
    setBillingPage(undefined);
    setBillingError('');
    setBillingLoading(true);
    void getAccountUsage(undefined, 20, abortController.signal).then((page) => {
      if (!abortController.signal.aborted) setBillingPage(page);
    }).catch((cause) => {
      if (!abortController.signal.aborted && !isAbortError(cause)) {
        setBillingError(cause instanceof Error ? cause.message : '无法读取消费记录。');
      }
    }).finally(() => {
      if (!abortController.signal.aborted) {
        if (billingAbort.current === abortController) billingAbort.current = undefined;
        setBillingLoading(false);
      }
    });
    return () => abortController.abort();
  }, [route.view, identityReady, billingIdentity]);

  useEffect(() => {
    let active = true;
    Promise.all([getAuthConfig(), getAuthMe()]).then(([config, me]) => {
      if (!active) return;
      setAuthConfig(config);
      setTurnstileSiteKey(config.turnstileSiteKey);
      advanceAccountIdentity();
      setAccount(me);
      setIdentityReady(true);
      void loadFirstProjectPage();
    }).catch((cause) => {
      if (!active) return;
      advanceAccountIdentity();
      setIdentityReady(true);
      setError(cause instanceof Error ? cause.message : '无法读取登录状态');
      void loadFirstProjectPage();
    });
    return () => {
      active = false;
      advanceAccountIdentity();
    };
  }, []);

  useEffect(() => {
    if (!identityReady) return;
    const controller = new AbortController();
    void listActiveGenerationTasks(controller.signal).then(async (tasks) => {
      const task = tasks[0];
      if (!task || controller.signal.aborted) return;
      setGenerationProgress(task);
      const finished = await pollGenerationTask(task.id, getGenerationTask, {
        signal: controller.signal,
        onProgress: setGenerationProgress,
      });
      if (controller.signal.aborted) return;
      setGenerationProgress(null);
      if (finished.projectId) {
        const project = await getProject(finished.projectId, controller.signal);
        if (!controller.signal.aborted) acceptProjectRevision(project, true);
      }
      void loadFirstProjectPage();
    }).catch((cause) => {
      if (!controller.signal.aborted && !isAbortError(cause)) setGenerationProgress(null);
    });
    return () => controller.abort();
  }, [identityReady]);

  useEffect(() => {
    referenceAssetsAbort.current?.abort();
    const projectId = current?.id;
    if (!identityReady || !projectId || !displayFeatures.image.enabled) {
      requestGate.current.invalidate('references');
      referenceAssetsAbort.current = undefined;
      setReferenceAssets([]);
      return;
    }
    const token = requestGate.current.begin('references');
    const abortController = new AbortController();
    referenceAssetsAbort.current = abortController;
    listReferenceAssets(projectId, abortController.signal).then((assets) => {
      if (requestGate.current.isCurrent('references', token)) setReferenceAssets(assets);
    }).catch((cause) => {
      if (!requestGate.current.isCurrent('references', token) || isAbortError(cause)) return;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经不存在，已从列表移除。')) {
        setError(cause instanceof Error ? cause.message : '无法读取参考图');
      }
    });
    return () => abortController.abort();
  }, [current?.id, identityReady, displayFeatures.image.enabled]);

  useEffect(() => {
    selectionAbort.current?.abort();
    if (!identityReady || !routeProjectId) {
      requestGate.current.invalidate('detail');
      selectionAbort.current = undefined;
      setCurrent(undefined);
      setProjectDetailLoading(false);
      setProjectDetailError('');
      return;
    }
    const token = requestGate.current.begin('detail');
    const projectId = routeProjectId;
    const cached = reusableCachedProject(detailCache.current.get(projectId), projectId);
    setCurrent(cached);
    setError('');
    setProjectDetailError('');
    const abortController = new AbortController();
    selectionAbort.current = abortController;
    setProjectDetailLoading(true);
    setImageBusySceneIndex(null);
    void getProject(projectId, abortController.signal).then((loaded) => {
      if (requestGate.current.isCurrent('detail', token) && activeRouteProjectId.current === projectId) {
        if (acceptProjectDetail(loaded, token.identityEpoch)) {
          setProjectDetailError('');
        } else {
          setProjectDetailError('故事列表显示了更新版本，请重试读取最新内容。');
        }
      }
    }).catch((cause) => {
      if (!requestGate.current.isCurrent('detail', token) || activeRouteProjectId.current !== projectId || isAbortError(cause)) return;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经不存在，已从列表移除。')) {
        setProjectDetailError(cause instanceof Error ? cause.message : '无法读取故事，请重试。');
      }
    }).finally(() => {
      if (requestGate.current.isCurrent('detail', token) && activeRouteProjectId.current === projectId) {
        if (selectionAbort.current === abortController) {
          selectionAbort.current = undefined;
        }
        setProjectDetailLoading(false);
      }
    });
    return () => abortController.abort();
  }, [routeProjectId, identityReady, projectDetailRetryNonce]);

  const routedCurrent = routeProjectId ? reusableCachedProject(current, routeProjectId) : undefined;
  const childScopeIdentityEpoch = captureIdentity();
  const childRequestScope = useMemo(
    () => routedCurrent
      ? responseCoordinator.current.createChildScope(childScopeIdentityEpoch, routedCurrent)
      : undefined,
    [childScopeIdentityEpoch, routedCurrent],
  );
  const currentLabel = useMemo(() => {
    if (route.view === 'billing') return '消费记录';
    if (routedCurrent) return routedCurrent.title;
    if (routeProjectId) return projects.find((project) => project.id === routeProjectId)?.title ?? '读取故事';
    return '新故事';
  }, [projects, route.view, routeProjectId, routedCurrent]);

  async function loadMoreBilling() {
    if (!billingPage?.nextCursor || billingLoadingMore || billingLoading) return;
    const cursor = billingPage.nextCursor;
    billingAbort.current?.abort();
    const abortController = new AbortController();
    billingAbort.current = abortController;
    setBillingError('');
    setBillingLoadingMore(true);
    try {
      const next = await getAccountUsage(cursor, 20, abortController.signal);
      if (abortController.signal.aborted) return;
      setBillingPage((current) => current ? {
        ...next,
        items: [...current.items, ...next.items],
      } : next);
    } catch (cause) {
      if (!abortController.signal.aborted && !isAbortError(cause)) {
        setBillingError(cause instanceof Error ? cause.message : '更多消费记录读取失败。');
      }
    } finally {
      if (!abortController.signal.aborted) {
        if (billingAbort.current === abortController) billingAbort.current = undefined;
        setBillingLoadingMore(false);
      }
    }
  }

  function commitProjectRevision(project: ClientProject, acceptedSummary: ClientProjectSummary, allowRailInsert = false) {
    detailCache.current.set(project.id, project);
    setProjects((items) => responseCoordinator.current.mergeDetail(acceptedSummary, items, allowRailInsert));
    if (activeRouteProjectId.current === project.id) setCurrent(project);
    return true;
  }

  function acceptProjectRevision(project: ClientProject, allowRailInsert = false) {
    const acceptedSummary = responseCoordinator.current.acceptDetail(project);
    if (!acceptedSummary) return false;
    return commitProjectRevision(project, acceptedSummary, allowRailInsert);
  }

  function acceptProjectDetail(project: ClientProject, originEpoch: number) {
    return isIdentityCurrent(originEpoch) && acceptProjectRevision(project);
  }

  function observeMutationProject(project: ClientProject, mutationToken: ProjectMutationToken) {
    if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
    if (mutationToken.projectId !== project.id && mutationToken.projectId !== CREATE_PROJECT_MUTATION_SCOPE) return false;
    return responseCoordinator.current.acceptDetail(project);
  }

  function acceptMutationProject(project: ClientProject, mutationToken: ProjectMutationToken, allowRailInsert = false) {
    const acceptedSummary = observeMutationProject(project, mutationToken);
    if (!acceptedSummary) return false;
    return commitProjectRevision(project, acceptedSummary, allowRailInsert);
  }

  function removeProjectFromClient(projectId: string, message?: string) {
    invalidateProjectListRequest();
    cancelProjectRecovery(projectId);
    requestGate.current.clearProjectState(projectId);
    responseCoordinator.current.clearProject(projectId);
    detailCache.current.delete(projectId);
    setProjects((items) => items.filter((project) => project.id !== projectId));
    clearReaderPosition(projectId);
    if (activeRouteProjectId.current === projectId) {
      setReferenceAssets([]);
      setCurrent(undefined);
      setProjectDetailLoading(false);
      setProjectDetailError('');
      setContinuing(false);
      setDeleting(false);
      setDeletingProject(false);
      setProjectEditBusy(false);
      setImageBusySceneIndex(null);
      setRewriteBusySceneIndex(null);
      navigate({ view: 'create' }, true);
    }
    if (message) setError(message);
  }

  function handleProjectNotFound(cause: unknown, projectId: string, message: string) {
    if (!isProjectNotFoundError(cause)) return false;
    removeProjectFromClient(projectId, message);
    return true;
  }

  function applyCurrentChildProjectResult<T>(scope: ChildProjectScope, apply: () => T) {
    return applyScopedChildProjectResult(scope, {
      identityEpoch: captureIdentity(),
      routeProjectId: activeRouteProjectId.current,
      project: detailCache.current.get(scope.projectId),
    }, apply);
  }

  function handleChildProjectNotFound(scope: ChildProjectScope, message: string) {
    applyCurrentChildProjectResult(scope, () => removeProjectFromClient(scope.projectId, message));
  }

  async function loadFirstProjectPage() {
    firstProjectPageAbort.current?.abort();
    appendProjectsAbort.current?.abort();
    appendProjectsAbort.current = undefined;
    const token = requestGate.current.beginFirstPage();
    const abortController = new AbortController();
    firstProjectPageAbort.current = abortController;
    setProjectListLoading(true);
    setLoadingMore(false);
    setProjectListError('');
    setProjectListRetryIntent(undefined);
    let firstPageSucceeded = false;
    try {
      const page = await listProjects(undefined, 20, abortController.signal);
      if (!requestGate.current.isCurrent('first-page', token)) return;
      const acceptedPage = responseCoordinator.current.acceptIncomingSummaries(page.projects);
      setProjects((items) => responseCoordinator.current.reconcileFirstPage(acceptedPage, items));
      setNextProjectCursor(page.nextCursor);
      firstPageSucceeded = true;
    } catch (cause) {
      if (requestGate.current.isCurrent('first-page', token) && !isAbortError(cause)) {
        setProjectListError(cause instanceof Error ? cause.message : '无法读取故事列表，请重试。');
        setProjectListRetryIntent({ kind: 'first-page' });
      }
    } finally {
      if (requestGate.current.isCurrent('first-page', token)) {
        requestGate.current.finishFirstPage(token, firstPageSucceeded);
        if (firstProjectPageAbort.current === abortController) firstProjectPageAbort.current = undefined;
        setProjectListLoading(false);
      }
    }
  }

  async function loadMoreProjects(retryCursor?: string) {
    if (projectListLoading || loadingMore || appendProjectsAbort.current || projectListRetryIntent?.kind === 'first-page') return;
    const cursor = retryCursor ?? nextProjectCursor;
    if (!cursor) return;
    const token = requestGate.current.beginAppend();
    if (!token) return;
    const abortController = new AbortController();
    appendProjectsAbort.current = abortController;
    setLoadingMore(true);
    setProjectListError('');
    setProjectListRetryIntent(undefined);
    try {
      const page = await listProjects(cursor, 20, abortController.signal);
      if (!requestGate.current.isCurrent('append', token)) return;
      const acceptedPage = responseCoordinator.current.acceptIncomingSummaries(page.projects);
      setProjects((items) => responseCoordinator.current.mergeAppend(acceptedPage, items));
      setNextProjectCursor(page.nextCursor);
    } catch (cause) {
      if (requestGate.current.isCurrent('append', token) && !isAbortError(cause)) {
        setProjectListError(cause instanceof Error ? cause.message : '更多故事读取失败，请重试。');
        setProjectListRetryIntent({ kind: 'append', cursor });
      }
    } finally {
      if (requestGate.current.isCurrent('append', token)) {
        if (appendProjectsAbort.current === abortController) appendProjectsAbort.current = undefined;
        setLoadingMore(false);
      }
    }
  }

  function retryProjectList() {
    if (!projectListRetryIntent) return;
    dispatchProjectListRetry(
      projectListRetryIntent,
      () => void loadFirstProjectPage(),
      (cursor) => void loadMoreProjects(cursor),
    );
  }

  function selectProject(projectId: string) {
    setError('');
    if (projectId === activeRouteProjectId.current && projectDetailError) {
      setProjectDetailRetryNonce((nonce) => nonce + 1);
      return;
    }
    navigate({ view: 'outline', projectId });
  }

  async function handleProjectImported(projectId?: string) {
    setProjectImportOpen(false);
    await loadFirstProjectPage();
    if (projectId) navigate({ view: 'outline', projectId });
  }

  function cancelPendingProjectDetail() {
    requestGate.current.invalidate('detail');
    selectionAbort.current?.abort();
    selectionAbort.current = undefined;
    setProjectDetailLoading(false);
  }

  function created(project: ClientProject, mutationToken: ProjectMutationToken) {
    if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return;
    cancelPendingProjectDetail();
    navigate({ view: 'outline', projectId: project.id });
    acceptMutationProject(project, mutationToken, true);
  }

  async function createFromStory(input: ProjectInput, model: TextModelId) {
    const mutationToken = requestGate.current.beginProjectMutation(CREATE_PROJECT_MUTATION_SCOPE);
    try {
      const pollOptions: GenerationTaskPollerOptions = { onProgress: setGenerationProgress };
      const project = await createProject(input, model, pollOptions);
      created(project, mutationToken);
    } catch (cause) {
      if (requestGate.current.isProjectMutationCurrent(mutationToken)) throw cause;
    } finally {
      setGenerationProgress(null);
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  function startNewStory() {
    cancelPendingProjectDetail();
    setCurrent(undefined);
    setImageBusySceneIndex(null);
    setRewriteBusySceneIndex(null);
    setReferenceAssets([]);
    navigate({ view: 'create' });
  }

  async function handleLogout() {
    setProfileModalOpen(false);
    const originEpoch = captureIdentity();
    try {
      await logoutApi();
      if (!isIdentityCurrent(originEpoch)) return;
      replaceAccountIdentity(DEFAULT_GUEST_ACCOUNT);
      await loadFirstProjectPage();
    } catch (cause) {
      if (isIdentityCurrent(originEpoch)) setError(cause instanceof Error ? cause.message : '退出登录失败');
    }
  }

  function handleAuthenticated(nextAccount: Extract<AuthMe, { authenticated: true }>) {
    replaceAccountIdentity(nextAccount);
    setAuthModalOpen(false);
    void loadFirstProjectPage();
  }

  function replaceAccountIdentity(nextAccount: AuthMe) {
    advanceAccountIdentity();
    detailCache.current.clear();
    setCurrent(undefined);
    setProjects([]);
    setNextProjectCursor(undefined);
    setProjectListError('');
    setProjectListRetryIntent(undefined);
    setProjectDetailError('');
    setAccount(nextAccount);
    setContinuing(false);
    setDeleting(false);
    setDeletingProject(false);
    setProjectEditBusy(false);
    setImageBusySceneIndex(null);
    setRewriteBusySceneIndex(null);
    startNewStory();
  }

  function handleAccountAction() {
    if (account.authenticated) {
      setProfileModalOpen(true);
      return;
    }
    setAuthModalOpen(true);
  }

  function openReader(sceneIndex?: number) {
    if (!current) return;
    navigate({ view: 'read', projectId: current.id, ...(sceneIndex === undefined ? {} : { sceneIndex }) });
  }

  async function uploadCurrentReference(dataUrl: string, label: string) {
    if (!current) throw new Error('请先打开一部小说');
    const originEpoch = captureIdentity();
    const projectId = current.id;
    try {
      const asset = await uploadReferenceAsset(projectId, dataUrl, label);
      if (!isIdentityCurrent(originEpoch)) throw new DOMException('账号已切换', 'AbortError');
      if (activeRouteProjectId.current === projectId) setReferenceAssets((items) => [asset, ...items.filter((item) => item.id !== asset.id)]);
      return asset;
    } catch (cause) {
      if (isIdentityCurrent(originEpoch)) handleProjectNotFound(cause, projectId, '这部小说已经不存在，无法上传参考图。');
      throw cause;
    }
  }

  async function refreshProjectAfterMutationFailure(projectId: string, mutationToken: ProjectMutationToken) {
    if (!requestGate.current.isProjectMutationCurrent(mutationToken) || activeRouteProjectId.current !== projectId) return false;
    projectRecoveryAborts.current.get(projectId)?.abort();
    const recoveryToken = requestGate.current.beginProjectRecovery(projectId);
    const abortController = new AbortController();
    projectRecoveryAborts.current.set(projectId, abortController);
    try {
      const latest = await getProject(projectId, abortController.signal);
      if (!requestGate.current.isProjectRecoveryCurrent(recoveryToken)
        || !requestGate.current.isProjectMutationCurrent(mutationToken)
        || activeRouteProjectId.current !== projectId) return false;
      return acceptProjectDetail(latest, recoveryToken.identityEpoch);
    } catch (cause) {
      if (requestGate.current.isProjectRecoveryCurrent(recoveryToken)
        && requestGate.current.isProjectMutationCurrent(mutationToken)
        && activeRouteProjectId.current === projectId
        && !isAbortError(cause)) {
        handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法恢复最新内容。');
      }
      return false;
    } finally {
      requestGate.current.finishProjectRecovery(recoveryToken);
      if (projectRecoveryAborts.current.get(projectId) === abortController) {
        projectRecoveryAborts.current.delete(projectId);
      }
    }
  }

  async function saveProjectEdits(draft: ProjectEditorDraft) {
    if (!current || projectEditBusy) return false;
    const projectId = current.id;
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setProjectEditBusy(false));
    cancelPendingProjectDetail();
    setError('');
    setProjectEditBusy(true);
    try {
      const updated = await updateProject(projectId, draft.title, draft.summary);
      return acceptMutationProject(updated, mutationToken);
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法保存修改。')) {
        setError(cause instanceof Error ? cause.message : '小说资料保存失败');
      }
      return false;
    } finally {
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  async function saveCharacterConfig(characterIndex: number | null, draft: CharacterConfigDraft) {
    if (!current || projectEditBusy) return false;
    const projectId = current.id;
    const projectSnapshot = current;
    const duplicate = projectSnapshot.characters.some((character, index) => character.name === draft.name && index !== characterIndex);
    if (duplicate) {
      setError(`角色“${draft.name}”已经存在，请换一个名字。`);
      return false;
    }
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setProjectEditBusy(false));
    cancelPendingProjectDetail();
    setError('');
    setProjectEditBusy(true);
    try {
      const previousName = characterIndex === null ? undefined : projectSnapshot.characters[characterIndex]?.name;
      let updated = characterIndex === null
        ? await addCharacter(projectId, { name: draft.name, role: draft.role, description: draft.description, gender: draft.gender, voiceTraits: draft.voiceTraits })
        : await updateCharacter(projectId, characterIndex, { name: draft.name, role: draft.role, description: draft.description, gender: draft.gender, voiceTraits: draft.voiceTraits });
      if (!observeMutationProject(updated, mutationToken)) return false;
      const targetIndex = characterIndex === null ? updated.characters.length - 1 : characterIndex;
      if (updated.characters[targetIndex]?.imageAssetId !== draft.imageAssetId) {
        updated = await bindCharacterImage(projectId, targetIndex, draft.imageAssetId || null);
        if (!observeMutationProject(updated, mutationToken)) return false;
      }
      const audioConfig = setCharacterVoice(projectSnapshot.audioConfig, previousName, draft.name, draft.voice);
      updated = await updateProjectAudioConfig(projectId, audioConfig);
      return acceptMutationProject(updated, mutationToken);
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      await refreshProjectAfterMutationFailure(projectId, mutationToken);
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法保存角色。')) {
        setError(cause instanceof Error ? cause.message : '角色配置保存失败');
      }
      return false;
    } finally {
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  async function deleteCurrentCharacterConfig(characterIndex: number) {
    if (!current || projectEditBusy || current.characters.length <= 1) return false;
    const projectId = current.id;
    const removedName = current.characters[characterIndex]?.name;
    const projectSnapshot = current;
    if (!removedName) return false;
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setProjectEditBusy(false));
    cancelPendingProjectDetail();
    setError('');
    setProjectEditBusy(true);
    try {
      let updated = await deleteCharacter(projectId, characterIndex);
      if (!observeMutationProject(updated, mutationToken)) return false;
      const audioConfig = removeCharacterVoice(projectSnapshot.audioConfig, removedName);
      updated = await updateProjectAudioConfig(projectId, audioConfig);
      return acceptMutationProject(updated, mutationToken);
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      await refreshProjectAfterMutationFailure(projectId, mutationToken);
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法删除角色。')) {
        setError(cause instanceof Error ? cause.message : '角色删除失败');
      }
      return false;
    } finally {
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  async function saveNarratorVoice(voice: GeminiVoiceId) {
    if (!current || projectEditBusy) return false;
    const projectId = current.id;
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setProjectEditBusy(false));
    cancelPendingProjectDetail();
    setError('');
    setProjectEditBusy(true);
    try {
      const audioConfig: ProjectAudioConfig = { ...current.audioConfig, narrator: voice };
      const updated = await updateProjectAudioConfig(projectId, audioConfig);
      return acceptMutationProject(updated, mutationToken);
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法保存旁白音色。')) {
        setError(cause instanceof Error ? cause.message : '旁白音色保存失败');
      }
      return false;
    } finally {
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  async function rewriteCurrentScene(sceneIndex: number, request: SceneEditRequest, onError?: ModalErrorSink) {
    if (!current || rewriteBusySceneIndex !== null) return false;
    const projectId = current.id;
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setRewriteBusySceneIndex(null));
    cancelPendingProjectDetail();
    setError('');
    setRewriteBusySceneIndex(sceneIndex);
    try {
      const updated = await editScene(projectId, sceneIndex, request, { onProgress: setGenerationProgress });
      if (!acceptMutationProject(updated, mutationToken)) return false;
      return true;
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法重置本幕。')) {
        const message = cause instanceof Error ? cause.message : '本幕重置失败';
        if (onError) onError(message);
        else setError(message);
      }
      return false;
    } finally {
      setGenerationProgress(null);
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  async function generateOneSceneImage(requestScope: ChildProjectScope, sceneIndex: number, source: SceneImageSource, model: SceneImageModel, customPrompt?: string, referenceImageDataUrl?: string, referenceAssetIds?: string[], signal?: AbortSignal): Promise<ImageGenerationErrorResponse | null | undefined> {
    const canApplyResult = () => applyCurrentChildProjectResult(requestScope, () => true) === true;
    if (!canApplyResult()) return undefined;
    if (imageBusySceneIndex !== null || imageBusyOperations.current.isBusy()) return toImageErrorResponse(new Error('当前已有图片生成任务，请稍候。'));
    const projectId = requestScope.projectId;
    const busyOperation = imageBusyOperations.current.beginBusy();
    cancelPendingProjectDetail();
    setError('');
    setImageBusySceneIndex(sceneIndex);
    try {
      await generateSceneImage(projectId, sceneIndex, source, model, customPrompt, referenceImageDataUrl, referenceAssetIds, signal, { signal, onProgress: setGenerationProgress });
      return applyCurrentChildProjectResult(requestScope, () => null);
    } catch (cause) {
      if (!canApplyResult() || isAbortError(cause)) return undefined;
      if (handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法生成场景图。')) return undefined;
      return toImageErrorResponse(cause);
    } finally {
      setGenerationProgress(null);
      if (imageBusyOperations.current.finishBusy(busyOperation)) setImageBusySceneIndex(null);
    }
  }

  async function continueCurrent(instruction: string | undefined, length: StoryLength, model: TextModelId, onError?: ModalErrorSink) {
    if (!current || continuing) return false;
    const projectId = current.id;
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setContinuing(false));
    cancelPendingProjectDetail();
    setError('');
    setContinuing(true);
    try {
      const updated = await continueProject(projectId, length, instruction, model, { onProgress: setGenerationProgress });
      return acceptMutationProject(updated, mutationToken);
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法继续生成。')) {
        const message = cause instanceof Error ? cause.message : '续写失败';
        if (onError) onError(message);
        else setError(message);
      }
      return false;
    } finally {
      setGenerationProgress(null);
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  async function deleteFromScene(sceneIndex: number) {
    if (!current || deleting) return false;
    const projectId = current.id;
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setDeleting(false));
    cancelPendingProjectDetail();
    setError('');
    setDeleting(true);
    try {
      const updated = await deleteScenesFromApi(projectId, sceneIndex);
      if (!acceptMutationProject(updated, mutationToken)) return false;
      if (updated.scenes.length === 0) navigate({ view: 'outline', projectId: updated.id }, true);
      return true;
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经被删除，无法删除章节。')) {
        setError(cause instanceof Error ? cause.message : '删除章节失败');
      }
      return false;
    } finally {
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  async function deleteCurrentProject() {
    if (!current || deletingProject) return false;
    const projectId = current.id;
    const mutationToken = requestGate.current.beginProjectMutation(projectId, () => setDeletingProject(false));
    cancelPendingProjectDetail();
    setError('');
    setDeletingProject(true);
    try {
      await deleteProjectApi(projectId);
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      removeProjectFromClient(projectId);
      void loadFirstProjectPage();
      return true;
    } catch (cause) {
      if (!requestGate.current.isProjectMutationCurrent(mutationToken)) return false;
      if (!handleProjectNotFound(cause, projectId, '这部小说已经不存在。')) {
        invalidateProjectListRequest();
        setError(cause instanceof Error ? cause.message : '删除小说失败');
      }
      void loadFirstProjectPage();
      return false;
    } finally {
      requestGate.current.finishProjectMutation(mutationToken);
    }
  }

  return (
    <div className="app-layout">
      <ProjectRail
        projects={projects}
        currentId={routeProjectId}
        account={account}
        phoneLogin={authConfig.phoneLogin}
        canTransfer={canTransfer}
        loading={projectListLoading}
        error={projectListError}
        nextCursor={nextProjectCursor}
        loadingMore={loadingMore}
        loadMoreBlocked={projectListRetryIntent?.kind === 'first-page'}
        onSelect={selectProject}
        onRetry={retryProjectList}
        onLoadMore={() => void loadMoreProjects()}
        onNew={startNewStory}
        onHome={startNewStory}
        onAccountAction={handleAccountAction}
        onImport={() => setProjectImportOpen(true)}
      />
      <main className="main-stage">
        <div className="topline"><span>{currentLabel}</span></div>
        {generationProgress && !generationProgressInModal && <div className="sync-strip" role="status"><span className="sync-spinner" />{generationProgress.status === 'queued' ? '已加入生成队列' : `正在生成… ${generationProgress.progress}%`}</div>}
        {projectDetailLoading && routedCurrent && !generationProgress && <div className="sync-strip" role="status"><span className="sync-spinner" />正在同步最新内容…</div>}
        {error && <StatusMessage message={error} />}
        {routeProjectId && projectDetailError && <div className="project-detail-error" role="alert"><p>{projectDetailError}</p><button aria-label="重试读取故事详情" type="button" onClick={() => setProjectDetailRetryNonce((nonce) => nonce + 1)}>重试</button></div>}
        {route.view === 'create' && <StoryForm onCreate={createFromStory} onRequireLogin={!account.authenticated ? () => setAuthModalOpen(true) : undefined} guestTrialAvailable={account.generationAccess.guestTrialAvailable} requiresLogin={account.generationAccess.requiresLogin} requiresBalance={account.generationAccess.requiresBalance} storyLengths={account.storyLengths ?? ['short']} />}
        {route.view === 'billing' && <BillingPage page={billingPage} loading={billingLoading} loadingMore={billingLoadingMore} error={billingError} onBack={() => navigate({ view: 'create' })} onLoadMore={() => void loadMoreBilling()} />}
        {routeProjectId && projectDetailLoading && !routedCurrent && <div className="project-detail-skeleton" role="status" aria-label="正在读取故事详情"><span /><span /><span /></div>}
        {route.view === 'outline' && routedCurrent && <OutlineView project={routedCurrent} features={displayFeatures} storyLengths={account.storyLengths ?? ['short']} assets={referenceAssets} onRead={openReader} onDeleteFrom={deleteFromScene} onDeleteProject={deleteCurrentProject} onUploadReference={uploadCurrentReference} onRewrite={rewriteCurrentScene} onSaveProject={saveProjectEdits} onSaveCharacter={saveCharacterConfig} onDeleteCharacter={deleteCurrentCharacterConfig} onSaveNarrator={saveNarratorVoice} continuing={continuing} deleting={deleting} deletingProject={deletingProject} projectEditBusy={projectEditBusy} rewriteBusySceneIndex={rewriteBusySceneIndex} generationProgress={generationProgress} onExport={() => setProjectExport(routedCurrent)} onImport={() => setProjectImportOpen(true)} canTransfer={canTransfer} />}
        {route.view === 'read' && routedCurrent && childRequestScope && <ReaderView key={`${routedCurrent.id}-${route.sceneIndex ?? 'saved'}`} initialSceneIndex={route.sceneIndex} project={routedCurrent} features={displayFeatures} storyLengths={account.storyLengths ?? ['short']} assets={referenceAssets} requestScope={childRequestScope} onBack={() => navigate({ view: 'outline', projectId: routedCurrent.id })} onContinue={continueCurrent} onRequireLogin={!account.authenticated ? () => setAuthModalOpen(true) : undefined} onDeleteFrom={deleteFromScene} onUploadReference={uploadCurrentReference} onGenerateImage={generateOneSceneImage} onRewrite={rewriteCurrentScene} onSceneChange={(sceneIndex) => navigate({ view: 'read', projectId: routedCurrent.id, sceneIndex })} onProjectNotFound={handleChildProjectNotFound} imageBusy={imageBusySceneIndex !== null} rewriteBusySceneIndex={rewriteBusySceneIndex} generationProgress={generationProgress} continuing={continuing} deleting={deleting} />}
      </main>
      {authModalOpen && <AuthModal onClose={() => setAuthModalOpen(false)} onAuthenticated={handleAuthenticated} />}
      {profileModalOpen && account.authenticated && <ProfileModal account={account} onClose={() => setProfileModalOpen(false)} onSaved={setAccount} onLogout={handleLogout} onOpenBilling={() => navigate({ view: 'billing' })} />}
      {canTransfer && projectExport && <ProjectExportModal projectId={projectExport.id} projectTitle={projectExport.title} onClose={() => setProjectExport(undefined)} />}
      {canTransfer && projectImportOpen && <ProjectImportModal onClose={() => setProjectImportOpen(false)} onImported={handleProjectImported} />}
    </div>
  );
}
